import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, ToastController } from '@ionic/angular';
import { firstValueFrom, Subscription } from 'rxjs';
import { NaploService } from '../naplo.service';
import { naploNyomtat } from '../naplo-nyomtatas';
import { bankVan } from '../naplo-menu';
import { osaSzamlakepNyit } from '../szamlakep-ablak';
import { DataService } from '../../data.service';
import { KeretService } from '../../keret.service';
import { KivonatSzuro, uresKivonatSzuro, kivonatQuery, kivonatCimkek } from '../kivonat-szukites/kivonat-szukites.component';
import { SzerkZar, SzerkZarService } from '../../components/szerk-zar/szerk-zar.service';

type Ful = 'penzugyi' | 'bank' | 'naplofokonyv' | 'hazipenztar' | 'vegyes' | 'nyito' | 'zaro' | 'osszes'
  | 'kivonat' | 'karton' | 'gyorsmerleg' | 'afa' | 'eszkoz';

/** Egy rácsoszlop. A `cs` az oszlopcsoport színe (pénz / ÁFA / deviza), az `osszeg` az Összegzés-sorba kerül. */
interface Oszlop { k: string; c: string; num?: boolean; cs?: 'cs-penz' | 'cs-afa' | 'cs-deviza'; osszeg?: boolean; szeles?: boolean; cim?: string; }

/** Egy sor-művelet a rács alatti gombsorban (RLB mintájára). */
interface Muvelet { k: string; c: string; ikon: string; sorKell: boolean; tilt?: (r: any) => string | null; }

type ModalTipus = 'jegyzet' | 'kiegyKonyv' | 'kiegyBeall' | 'rendezesek' | 'bankTortenet' | 'osszepontozas' | 'kiegySzerk' | 'bankSzerk';

const NAPLO_BETU: Record<string, string> = { vevo: 'K', szallito: 'S', penztar: 'P', bank: 'B', vegyes: 'V', nyito: 'N', zaro: 'Z' };
const NAPLO_NEV: Record<string, string> = { vevo: 'Vevő', szallito: 'Szállító', penztar: 'Pénztár', bank: 'Bank', vegyes: 'Vegyes', nyito: 'Nyitó', zaro: 'Záró' };

/**
 *  NAPLÓK – egy menüpont, fülekkel (a könyvelőprogramok naplóinak mintájára):
 *
 *    - PÉNZÜGYI NYILVÁNTARTÁS: a vevő- és szállítószámlák rendezettsége (végösszeg, rendezve,
 *      hátralék, lejárat), devizásnál a deviza-oszlopokkal.
 *    - PÉNZTÁR/BANK NAPLÓ: a bankszámlák tételei (a banki szinkronból) és – egyszeres
 *      könyvelésnél – a könyvelt pénztári tételek, NYITÓ és ZÁRÓ egyenleggel soronként.
 *    - Kettősnél: VEGYES, NYITÓ, ZÁRÓ és ÖSSZES napló (a bizonylatok), FŐKÖNYVI KIVONAT,
 *      FŐKÖNYVI KARTONOK, GYORSMÉRLEG, HÁZIPÉNZTÁR (a pénztár-számla kartonja), ÁFA-ANALITIKA.
 *    - Egyszeresnél: NAPLÓFŐKÖNYV és HÁZIPÉNZTÁR.
 *    - Mindkettőnél: BEFEKTETETT ESZKÖZÖK – a Tárgyi eszköz modul adatából az év eszközmozgásai
 *      időrendben (napló), vagy csoportonként nyitó / növekedés / csökkenés / záró (mozgástábla).
 *
 *  Minden fül ugyanazt a keretet kapja: bal oldalt műveletsáv (Szűkítés, Keresés, Összegzés,
 *  Nyomtatás, Exportálás), felül gyorskereső, alatta a rács, legalul a SOR-MŰVELETEK gombsora
 *  (egy sorra kattintva: módosítás, törlés, kiegyenlítés, rendezések, számlakép, jegyzet …).
 *  A rács oszlopai adatként vannak megadva, így a keresés, az összegzés, a nyomtatás és az
 *  export egy helyen működik mindegyik fülre. A műveletek kiszolgálói oldala: naplo-naplok.js.
 */
/** A Naplók rácsa egyszerre ennyi sort rajzol ki (a többi görgetésre jön – lásd rajzoltSorok). */
const RACS_LAP = 200;

@Component({
  selector: 'app-naplo-naplok',
  templateUrl: './naplok.component.html',
  styleUrls: ['../naplo-shared.scss', './naplok.component.scss'],
})
export class NaplokComponent implements OnInit, OnDestroy {
  @ViewChild('keresoMezo') keresoMezo?: ElementRef<HTMLInputElement>;

  ful: Ful = 'naplofokonyv';
  kereses = '';
  keresesMod: 'eleje' | 'tartalmaz' = 'tartalmaz';
  /** A mezőben látható szöveg – a szűrés (`kereses`) gépelés után kis késleltetéssel követi (keresesValt). */
  keresesBe = '';
  private keresesIdozito: any = null;
  szukitesNyit = false;
  /*  Főkönyvi kivonat: az RLB „Szűkítés" ablaka (közös komponens). A kivonat fülön a műveletsáv
   *  Szűkítés gombja ezt nyitja a sima időszak-sáv helyett. */
  kivonatSzuro: KivonatSzuro = uresKivonatSzuro();
  kivonatSzukitesNyitva = false;
  get kivonatSzuroCimkek(): string[] { return kivonatCimkek(this.kivonatSzuro); }

  /*  A TÖBBI NAPLÓ (bank, pénzügyi, vegyes, nyitó, záró, összes, karton) ugyanazzal az ablakkal
   *  szűkíthető – ott a feltételek a már betöltött sorokra hatnak (a kivonatot a szerver szűri). */
  listaSzuro: KivonatSzuro = uresKivonatSzuro();
  private partnerKodNev = new Map<string, string>();
  get szukitesLehet(): boolean { return !['gyorsmerleg', 'afa'].includes(this.ful) && !(this.ful === 'eszkoz' && this.eszkozNezet === 'tabla'); }
  get aktivSzuro(): KivonatSzuro { return this.ful === 'kivonat' ? this.kivonatSzuro : this.listaSzuro; }
  get aktivCimkek(): string[] { return kivonatCimkek(this.aktivSzuro); }
  get szukitesMezok(): string[] | null {
    switch (this.ful) {
      case 'kivonat': return null;
      case 'bank': return ['datum', 'szamla', 'partner', 'deviza'];
      case 'penzugyi': return ['datum', 'partner', 'deviza'];
      case 'karton': case 'vegyes': case 'nyito': case 'zaro': case 'osszes': return ['datum', 'szamla', 'munkaszam', 'partner', 'deviza'];
      case 'hazipenztar': return this.kettos ? ['datum', 'munkaszam', 'partner'] : ['datum', 'partner'];
      case 'eszkoz': return this.kettos ? ['datum', 'szamla', 'partner'] : ['datum', 'partner'];
      default: return ['datum', 'partner'];
    }
  }
  szukitesGomb() { if (this.szukitesLehet) this.kivonatSzukitesNyitva = true; }
  async szukitesKesz(sz: KivonatSzuro) {
    if (this.ful === 'kivonat') { await this.kivonatSzukit(sz); return; }
    this.listaSzuro = { ...sz };
    this.szuro.tol = sz.tol; this.szuro.ig = sz.ig;       // a dátumot a rácsépítők maguk is nézik
    this.kivonatSzukitesNyitva = false;
    if (sz.partner_kod && !this.partnerKodNev.size && this.aktiv.ugyfelId) {
      const p = await this.naplo.get<any[]>(`/partnerek/${this.aktiv.ugyfelId}`).catch(() => []);
      this.partnerKodNev = new Map((p || []).map((x: any) => [String(x.kod), String(x.nev || '')]));
    }
    this.epit();
  }
  async szuroTorol() {
    if (this.ful === 'kivonat') { await this.kivonatSzuroTorol(); return; }
    this.listaSzuro = uresKivonatSzuro(); this.szuro.tol = ''; this.szuro.ig = '';
    this.epit();
  }
  private illesz(ertek: string, minta: string, op: string): boolean {
    const e = String(ertek || '').toLowerCase(), m = String(minta || '').toLowerCase();
    return op === 'pontos' ? e === m : op === 'tartalmaz' ? e.includes(m) : e.startsWith(m);
  }
  //  Intervallum: a felső határt feltöltjük, hogy a „4" a 4542-t is fogja (mint a szerveren).
  private kozte(v: string, tol: string, ig: string): boolean { return (!tol || v >= tol) && (!ig || v <= ig + '999999999'); }
  /** Megfelel-e egy (adat)sor a lista-szűkítésnek. A rácsépítők a `_szlak` / `_msz` / `_dev` mezőkben adják át, amit tudnak. */
  listaIllik(r: any): boolean {
    const s = this.listaSzuro;
    const szlak: string[] = r._szlak || [r.fksz, r.ellen, r._szla].filter(Boolean).map(String);
    if (s.szamla && !szlak.some((x) => this.illesz(x, s.szamla, s.szamla_op))) return false;
    if ((s.szamla_tol || s.szamla_ig) && !szlak.some((x) => this.kozte(x, s.szamla_tol, s.szamla_ig))) return false;
    const msz: string[] = r._msz || [];
    if (s.msz_nelkul && msz.length) return false;
    if (s.munkaszam && !msz.some((x) => this.illesz(x, s.munkaszam, s.munkaszam_op))) return false;
    if ((s.msz_tol || s.msz_ig) && !msz.some((x) => this.kozte(x, s.msz_tol, s.msz_ig))) return false;
    const pnev = String(r._partner ?? r.partner ?? '');
    if (s.partner_kod) {
      const n = this.partnerKodNev.get(String(s.partner_kod).trim());
      if (!n || pnev.trim().toLowerCase() !== n.trim().toLowerCase()) return false;
    }
    if (s.partner_nev && !this.illesz(pnev, s.partner_nev, s.partner_nev_op)) return false;
    const dev = String(r._dev ?? r.devnem ?? r.deviza ?? '') || 'HUF';
    if (s.deviza && dev !== s.deviza) return false;
    return true;
  }
  async kivonatSzukit(sz: KivonatSzuro) {
    this.kivonatSzuro = { ...sz, mind: this.kivonatSzuro.mind };
    this.kivonatSzukitesNyitva = false;
    await this.betolt();
  }
  async kivonatMindValt(v: boolean) { this.kivonatSzuro = { ...this.kivonatSzuro, mind: v }; await this.betolt(); }
  async kivonatSzuroTorol() { this.kivonatSzuro = { ...uresKivonatSzuro(), mind: this.kivonatSzuro.mind }; await this.betolt(); }
  osszegzes = true;
  negativ = true;                  // kiadás / szállítói számla negatív előjellel
  betoltes = false;
  hiba = '';

  szuro = { tol: '', ig: '', forras: '', statusz: '', tipus: '', allapot: '', szamla: '*', szlaTol: '', szlaIg: '' };

  // nyers adatok
  private bankSzamlak: any[] = [];
  private bankTetelek: any[] = [];
  private kiegy: any[] = [];
  private nfk: any = null;          // naplófőkönyv (egyszeres)
  private kivonat: any = null;      // főkönyvi kivonat (kettős)
  private bizonylatok: any[] = [];  // kettős bizonylatok (vegyes / nyitó / záró / összes)
  private karton: any = null;
  /*  Kettősnél a bankszámlához rendelt főkönyvi szám NYITÓ tételei (bankszámlánként). A bank a
   *  szinkronnal nem közli az egyenleget, ezért a nyitó csak a főkönyvből jöhet – enélkül a
   *  bank napló 0-ról indult, miközben a pénztár (ami kartonból épül) jól mutatta a nyitót. */
  private bankNyitok: { bank_id: string; fksz: string; sorok: any[]; kezi: any[] }[] = [];
  /** Kettős: a tükör bankszámlái (384X pénzszámla), amelyekhez nincs banki kapcsolat (naplo_bank) – a Bank nézet
   *  forrásai között „fk:<számla>” azonosítóval, a kartonjukkal (a kézzel könyvelt banki bizonylatok, pl. előleg). */
  private bankFokSzamlak: { szamlaszam: string; nev: string }[] = [];
  private merleg: any = null;
  private afa: any = null;
  /*  BEFEKTETETT ESZKÖZÖK (backend: kettos-eszkoz.js befektetettNaplo). Két nézet: időrendi napló
   *  vagy csoportonkénti mozgástábla (a mérleg „A. Befektetett eszközök" tagolásában). */
  private eszkozAdat: any = null;
  eszkozNezet: 'naplo' | 'tabla' = 'naplo';
  eszkozNezetValt(v: 'naplo' | 'tabla') { this.eszkozNezet = v; this.kijelolt = null; this.epit(); }
  private kettosBeall: any = null;
  kartonSzamlak: { szamlaszam: string; nev: string }[] = [];

  oszlopok: Oszlop[] = [];
  sorok: any[] = [];                // a fül összes sora (szűrés előtt a kereséshez)
  /** Üres oszlopok mutatása. Alapból NEM: egy 16 oszlopos naplóban (pl. deviza, ÁFA-esedékesség)
   *  sokszor a fele üres, és miattuk a lényeges összeg-oszlopok kicsúsznak jobbra a képből. */
  uresOszlopok = false;
  kijelolt: any = null;             // a kiválasztott sor (a műveletek erre vonatkoznak)
  private jegyzetek = new Map<string, string>();
  private kijelolLevetel = false;
  private qSub?: Subscription;

  // modál-ablakok
  modal: ModalTipus | null = null;
  m: any = {};                      // a nyitott ablak adatai
  mFut = false;
  /*  SZERKESZTÉSI ZÁR (backend/szerkesztes-zar.js) az ablakban módosított banki bizonylatra / számlára –
   *  amíg más szerkeszti, nálunk csak olvasható. Az ablak bezárásakor (zar()) elenged. */
  zarBank: SzerkZar | null = null;
  zarKiegy: SzerkZar | null = null;
  private zarKor: Record<string, number> = {};
  /** Zár kérése; ha a válasz megérkezéséig az ablak már mást mutat (vagy bezárult), a késő zárat elengedjük. */
  private async zarKer(mezo: 'zarBank' | 'zarKiegy', tipus: string, id: any, ujratolt: () => unknown) {
    this.zarEngedd(mezo);
    const kor = this.zarKor[mezo];
    const z = await this.szerkZar.foglal('naplo', tipus, id, { ujratolt });
    if (kor !== this.zarKor[mezo]) { z?.felenged(); return; }
    this[mezo] = z;
  }
  private zarEngedd(mezo: 'zarBank' | 'zarKiegy') {
    this.zarKor[mezo] = (this.zarKor[mezo] || 0) + 1;
    this[mezo]?.felenged(); this[mezo] = null;
  }
  /** Más szerkeszti – nálunk nem menthető. */
  zarTilt(z: SzerkZar | null) { return !!z && !z.irhato; }

  constructor(private naplo: NaploService, private http: HttpClient, private route: ActivatedRoute, private router: Router,
              private data: DataService, private alert: AlertController, private toast: ToastController,
              private keret: KeretService, private szerkZar: SzerkZarService) {}

  get aktiv() { return this.naplo.aktiv; }

  /* ── RLB-IMPORT (pénzügyi nyilvántartás + pénztár-bank napló) – backend/naplo-rlb-import.js ── */
  rlb: any = { nyitva: false, fajlok: [], elonezet: null, eredmeny: null, felulir: {}, fut: false, rajta: false };
  rlbNyit() { this.rlb = { nyitva: true, fajlok: [], elonezet: null, eredmeny: null, felulir: {}, fut: false, rajta: false }; }
  /** Több fájl egyszerre (tallózás vagy ráhúzás); azonos nevűt nem veszünk fel kétszer. */
  rlbFajlok(lista: FileList | null | undefined) {
    for (const f of Array.from(lista || [])) {
      if (!/\.xlsx?$/i.test(f.name) || this.rlb.fajlok.some((x: File) => x.name === f.name)) continue;
      this.rlb.fajlok.push(f);
    }
    this.rlb.elonezet = null; this.rlb.eredmeny = null;
    if (this.rlb.fajlok.length) this.rlbElonezet();
  }
  rlbTorol(i: number) { this.rlb.fajlok.splice(i, 1); this.rlb.elonezet = null; if (this.rlb.fajlok.length) this.rlbElonezet(); }
  /** A kiszolgáló felismerése a fájlnév mellé (az előnézet után). */
  rlbFelismert(nev: string): string {
    const f = (this.rlb.elonezet?.fajlok || []).find((x: any) => x.nev === nev);
    if (!f) return this.rlb.elonezet ? '' : 'felismerés az előnézetnél';
    return ({ szamlak: '→ pénzügyi nyilvántartás', bank: '→ pénztár-bank napló', vegyes: '→ vegyes napló',
      sorok: '→ összes napló, számlasoros: az RLB pontos kontírozása', osszes: '→ összes napló (fejadat; a nyitótételek innen)',
      nyito: '→ nyitó napló: a nyitótételek (megnyitja az évet)',
      eszkoz: '→ eszközlista: a Tárgyi eszköz modulban importáld', ismeretlen: '→ nem ismerhető fel (kimarad)' } as any)[f.tipus] || f.tipus;
  }
  private rlbKuld(elonezet: boolean) {
    const fd = new FormData();
    for (const f of this.rlb.fajlok) fd.append('fajlok', f, f.name);
    const felulir = Object.fromEntries(Object.entries(this.rlb.felulir).filter(([, v]) => String(v || '').trim()));
    fd.append('felulir', JSON.stringify(felulir));
    const u = this.aktiv.ugyfelId;
    return firstValueFrom(this.http.post<any>(`/api/naplo-rlb-import/${u}?ugyfelId=${u}${elonezet ? '&elonezet=1' : ''}`, fd));
  }
  async rlbElonezet() {
    this.rlb.fut = true;
    try { this.rlb.elonezet = await this.rlbKuld(true); this.rlb.eredmeny = null; }
    catch (e: any) { (await this.toast.create({ message: e?.error?.error || 'Nem sikerült beolvasni.', duration: 3500, color: 'danger' })).present(); }
    finally { this.rlb.fut = false; }
  }
  async rlbImport() {
    this.rlb.fut = true;
    try {
      this.rlb.eredmeny = await this.rlbKuld(false);
      this.rlb.elonezet = null;
      await this.betolt();
    } catch (e: any) { (await this.toast.create({ message: e?.error?.error || 'Az import nem sikerült.', duration: 4000, color: 'danger' })).present(); }
    finally { this.rlb.fut = false; }
  }
  /** A korábbi RLB-import bizonylatainak törlése (az újrafuttatás előtt, pl. pontosabb exporttal). */
  async rlbVisszavon() {
    const a = await this.alert.create({
      header: 'Korábbi RLB-import visszavonása',
      message: 'Törlődik minden bizonylat, nyitott tétel és rendezés, ami az RLB-importból jött; a letöltött banki tételek újra kontírozatlanok lesznek. A kézzel rögzített könyvelés és a partnerek maradnak. Folytatod?',
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Visszavonás', role: 'destructive', handler: async () => {
        this.rlb.fut = true;
        try {
          const r: any = await firstValueFrom(this.http.delete<any>(`/api/naplo-rlb-import/${this.aktiv.ugyfelId}?ugyfelId=${this.aktiv.ugyfelId}`));
          (await this.toast.create({ message: `Visszavonva: ${r.bizonylat} bizonylat, ${r.kiegy} nyitott tétel; ${r.banki_visszaallitva} banki tétel újra kontírozatlan.`, duration: 4500, color: 'success' })).present();
          this.rlb.elonezet = null; this.rlb.eredmeny = null;
          await this.betolt();
          if (this.rlb.fajlok.length) await this.rlbElonezet();
        } catch (e: any) { (await this.toast.create({ message: e?.error?.error || 'Nem sikerült visszavonni.', duration: 4000, color: 'danger' })).present(); }
        finally { this.rlb.fut = false; }
      } }],
    });
    await a.present();
  }
  rlbNyitoSzoveg(n: any): string {
    return ({ pontos: `${n.db} nyitótétel az RLB kontírozásával – megnyitja az évet`, osztaly: `${n.db} nyitótétel – az oldalak a számlaosztályból, a 491 nullára zár`,
      nem_egyertelmu: `${n.db} nyitótétel – az oldalak nem egyértelműek (491: ${n.egyenleg}), kimarad`, mar_megnyitva: 'az év már meg van nyitva – a nyitótételek kimaradnak',
      nincs: 'nincs nyitótétel az importban' } as any)[n.allapot] || n.allapot;
  }
  get rlbUjSzamlak(): any[] { return (this.rlb.elonezet?.szamlak || []).filter((s: any) => s.allapot === 'uj'); }
  get rlbKimarado(): any[] {
    return (this.rlb.elonezet?.penzek || []).filter((p: any) => !['parosit', 'uj_tranzakcio', 'uj', 'uj_fuggo'].includes(p.allapot) && p.allapot !== 'mar_konyvelt');
  }
  //  Hivatkozás NÉLKÜLI pénztár/vegyes sorok, amelyek a függő technikai számlával BEKERÜLNEK.
  get rlbFuggo(): any[] {
    const p = (this.rlb.elonezet?.penzek || []).filter((x: any) => x.allapot === 'uj_fuggo');
    const v = (this.rlb.elonezet?.vegyesek || []).filter((x: any) => x.allapot === 'uj_fuggo');
    return [...p, ...v];
  }
  rlbAllapot(p: any): string {
    return ({ nincs_szamla: 'számla-hivatkozás nélkül / a hivatkozott számla nincs meg', nincs_bank: 'nincs ilyen bankszámla az ügyfélnél',
      eszkoz_modul: 'a Tárgyi eszköz modul könyveli (eszközök importja + écs-feladás)', kezi: 'kézzel kell könyvelni', par_nelkul: 'rendezés pár nélkül',
      uj_fuggo: 'bekerül – függő technikai ellenszámlával (az ellenszámla utólag pótolandó)' } as any)[p.allapot] || p.allapot;
  }
  rlbVegyesDb(allapot: string): number { return (this.rlb.elonezet?.vegyesek || []).filter((v: any) => v.allapot === allapot).length; }
  get rlbVegyesKimarad(): any[] {
    return (this.rlb.elonezet?.vegyesek || []).filter((v: any) => ['eszkoz_modul', 'kezi', 'par_nelkul', 'nincs_szamla'].includes(v.allapot));
  }
  get kettos() { return this.aktiv.konyveles_mod === 'kettos'; }

  get fulek(): { k: Ful; c: string }[] {
    if (this.kettos) {
      return [
        { k: 'penzugyi', c: 'Pénzügyi nyilvántartás' }, { k: 'bank', c: 'Pénztár/Bank napló' }, { k: 'vegyes', c: 'Vegyes napló' },
        { k: 'nyito', c: 'Nyitó napló' }, { k: 'zaro', c: 'Záró napló' }, { k: 'osszes', c: 'Összes napló' },
        { k: 'kivonat', c: 'Főkönyvi kivonat' }, { k: 'karton', c: 'Főkönyvi kartonok' }, { k: 'gyorsmerleg', c: 'Gyorsmérleg' },
        { k: 'afa', c: 'Áfa-analitika' }, { k: 'eszkoz', c: 'Befektetett eszközök' },
      ];
    }
    /*  EGYSZERES KÖNYVVITEL: nincs külön „pénzügyi nyilvántartás" – a pénzmozgást a
     *  NAPLÓFŐKÖNYV vezeti, a készpénzt a Házipénztár. A bank csak akkor, ha az ügyfélnél be van
     *  kapcsolva (Ügyfelek → Bank használata): sok cégnél nincs rá szükség. */
    return [
      { k: 'naplofokonyv', c: 'Naplófőkönyv' }, { k: 'hazipenztar', c: 'Házipénztár' },
      ...(bankVan(this.aktiv.konyveles_mod, this.aktiv.bank_naplo) ? [{ k: 'bank' as Ful, c: 'Bank napló' }] : []),
      { k: 'eszkoz', c: 'Befektetett eszközök' },
    ];
  }
  get fulCim(): string { return this.fulek.find((f) => f.k === this.ful)?.c || 'Napló'; }

  async ngOnInit() {
    await this.naplo.init();
    this.qSub = this.route.queryParamMap.subscribe(async (q) => {
      if (this.kijelolLevetel) { this.kijelolLevetel = false; return; }   // csak a `kijelol` paramétert vettük le
      let f = (q.get('ful') || (this.kettos ? 'penzugyi' : 'naplofokonyv')) as Ful;
      if (f === 'bank' && !bankVan(this.aktiv.konyveles_mod, this.aktiv.bank_naplo)) f = 'naplofokonyv';
      if (f === 'hazipenztar' && this.kettos) { f = 'bank'; this.szuro.forras = 'penztar'; }
      if (!this.fulek.some((x) => x.k === f)) f = this.kettos ? (f === 'naplofokonyv' ? 'kivonat' : 'penzugyi') : 'naplofokonyv';
      //  Az ÁFA-analitika minden megnyitáskor rákérdez az időszakra (hónap, negyedév vagy teljes év);
      //  a fülön belül (frissítés, szűrés, visszatérés ugyanide) a választott időszak megmarad.
      if (f === 'afa' && this.ful !== 'afa') { this.afaIdoszak = ''; this.afa = null; this.aranyositas = null; this.afaGyakorisagBetolt(); }
      this.ful = f;
      this.kereses = ''; this.keresesBe = ''; clearTimeout(this.keresesIdozito);
      this.kijelolt = null;
      if (q.get('szamla')) this.szuro.szamla = q.get('szamla') || '';
      if (q.get('forras')) this.szuro.forras = q.get('forras') || '';
      await this.betolt();
      //  A főmenü „RLB-import” csempéje ide hoz – az importablakkal.
      if (q.get('rlbimport') === '1' && this.kettos && !this.rlb.nyitva) {
        this.rlbNyit();
        this.router.navigate([], { relativeTo: this.route, queryParams: { rlbimport: null }, queryParamsHandling: 'merge', replaceUrl: true });
      }
      // Visszatérés a könyvelőablakból: a módosított / új tétel legyen kijelölve és látható.
      const kij = q.get('kijelol');
      if (kij) {
        this.kijelolt = this.sorok.find((r) => String(r._id) === kij || String(r._tetel) === kij) || null;
        setTimeout(() => document.querySelector('app-naplo-naplok tr.kijelolt')?.scrollIntoView({ block: 'center' }), 50);
        this.kijelolLevetel = true;
        this.router.navigate([], { relativeTo: this.route, queryParams: { kijelol: null }, queryParamsHandling: 'merge', replaceUrl: true });
      }
    });
  }
  ngOnDestroy() { this.qSub?.unsubscribe(); this.zarEngedd('zarBank'); this.zarEngedd('zarKiegy'); }

  fulValt(k: Ful, extra: Record<string, string> = {}) { this.router.navigate([], { relativeTo: this.route, queryParams: { ful: k, ...extra } }); }

  /* ================= betöltés ================= */

  private kGet<T = any>(p: string) { return firstValueFrom(this.http.get<T>('/api/kettos' + p)); }
  /** Minden napló sorszáma a keltezés szerint, elölről (a backend ezt minden könyvelés után is megteszi). */
  async ujraszamoz() {
    try {
      const r: any = await firstValueFrom(this.http.post(`/api/kettos/sorszam-rendez/${this.aktiv.ugyfelId}?ev=${this.aktiv.ev}`, {}));
      (await this.toast.create({ message: r?.valt ? `Újraszámozva: ${r.valt} bizonylat sorszáma változott.` : 'A sorszámok már a keltezés szerinti sorrendben vannak.', duration: 3500, color: 'success' })).present();
      await this.betolt();
    } catch (e: any) { (await this.toast.create({ message: e?.error?.error || 'Nem sikerült újraszámozni.', duration: 4000, color: 'danger' })).present(); }
  }
  private nn<T = any>(m: 'GET' | 'POST' | 'PUT' | 'DELETE', p: string, body?: any) {
    return firstValueFrom(this.http.request<T>(m, '/api/naplo-naplok' + p, { body }));
  }

  async betolt() {
    const u = this.aktiv.ugyfelId;
    if (!u) return;
    this.betoltes = true; this.hiba = '';
    const ev = this.aktiv.ev;
    try {
      if (this.ful === 'bank') {
        const [sz, t] = await Promise.all([
          firstValueFrom(this.http.get<any[]>(`/api/naplo-bank/${u}/szamlak`)).catch(() => []),
          firstValueFrom(this.http.get<any[]>(`/api/naplo-bank/${u}/tranzakciok`)).catch(() => []),
        ]);
        this.bankSzamlak = sz || [];
        this.bankTetelek = t || [];
        // Egyszeres könyvelésnél a pénztári tételek a naplófőkönyvből jönnek, kettősnél a
        // pénztár-számla (Könyvelési beállítások) kartonjából.
        if (!this.kettos) this.nfk = await this.naplo.get(`/naplofokonyv/${u}${this.naplo.evq()}`).catch(() => null);
        else if (!this.kettosBeall) this.kettosBeall = await this.kGet(`/beallitas/${u}`).catch(() => ({}));
        if (this.kettos) {
          const bankFk = new Set(this.bankSzamlak.map((b) => String(b.fokonyvi_szam || '').trim()).filter(Boolean));
          const tukor: any[] = await this.kGet<any[]>(`/szamlatukor/${u}?seed=0`).catch(() => []) || [];
          this.bankFokSzamlak = tukor
            .filter((x: any) => String(x.szamlaszam).startsWith('384') && !x.gyujto && (x.aktiv === undefined || x.aktiv) && !bankFk.has(String(x.szamlaszam)))
            .map((x: any) => ({ szamlaszam: String(x.szamlaszam), nev: x.nev || '' }));
        } else this.bankFokSzamlak = [];
        if (!this.szuro.forras || !this.forrasok.some((f) => f.id === this.szuro.forras)) {
          this.szuro.forras = this.forrasok[0]?.id || '';
        }
        if (this.kettos) await this.bankNyitokBetolt(u, ev);
        const kartonSzla = this.kartonForras(this.szuro.forras);
        if (this.kettos && kartonSzla) {
          this.karton = await this.kGet(`/fokonyvi-karton/${u}?ev=${ev}&szamla=${encodeURIComponent(kartonSzla)}`).catch(() => null);
        }
      } else if (this.ful === 'penzugyi') {
        this.kiegy = await this.naplo.get(`/kiegy/${u}${this.naplo.evq()}`);
      } else if (this.ful === 'naplofokonyv' || (this.ful === 'hazipenztar' && !this.kettos)) {
        this.nfk = await this.naplo.get(`/naplofokonyv/${u}${this.naplo.evq()}`);
      } else if (this.ful === 'kivonat') {
        this.kivonat = await this.kGet(`/fokonyvi-kivonat/${u}?${kivonatQuery(this.kivonatSzuro, ev)}`);
      } else if (['vegyes', 'nyito', 'zaro', 'osszes'].includes(this.ful)) {
        this.bizonylatok = await this.kGet<any[]>(`/tetelek/${u}?ev=${ev}${this.ful === 'osszes' ? '' : '&naplo=' + this.ful}`);
      } else if (this.ful === 'karton') {
        if (!this.kivonat) this.kivonat = await this.kGet(`/fokonyvi-kivonat/${u}?ev=${ev}`).catch(() => null);
        /*  Melyik számlát lehet választani. Elsősorban amin volt mozgás – de a GYŰJTŐ számlák is
         *  (az ő kartonjuk az alájuk tartozó számlák tételeit mutatja összevontan), és ha a
         *  kivonaton semmi sem mozgott, a teljes számlatükör, hogy a lista ne maradjon üresen. */
        const mozgott = (this.kivonat?.sorok || [])
          .filter((r: any) => Number(r.tartozik) || Number(r.kovetel) || Number(r.nyito))
          .map((r: any) => ({ szamlaszam: String(r.szamlaszam), nev: r.nev + (r.gyujto ? ' (gyűjtő – összevont)' : '') }));
        this.kartonSzamlak = mozgott.length ? mozgott
          : (await this.kGet<any[]>(`/szamlatukor/${u}?seed=0`).catch(() => []) || [])
            .filter((x: any) => x.aktiv === undefined || x.aktiv)
            .map((x: any) => ({ szamlaszam: String(x.szamlaszam), nev: x.nev + (x.gyujto ? ' (gyűjtő – összevont)' : '') }));
        if (!this.szuro.szamla) this.szuro.szamla = '*';
        const tart = this.szuro.szamla === '*' ? `&tol=${encodeURIComponent(this.szuro.szlaTol)}&ig=${encodeURIComponent(this.szuro.szlaIg)}` : '';
        this.karton = await this.kGet(`/fokonyvi-karton/${u}?ev=${ev}&szamla=${encodeURIComponent(this.szuro.szamla)}${tart}`);
      } else if (this.ful === 'hazipenztar') {
        if (!this.kettosBeall) this.kettosBeall = await this.kGet(`/beallitas/${u}`).catch(() => ({}));
        const szla = this.kettosBeall?.penztar_szamla;
        this.karton = szla ? await this.kGet(`/fokonyvi-karton/${u}?ev=${ev}&szamla=${encodeURIComponent(szla)}`) : null;
      } else if (this.ful === 'gyorsmerleg') {
        this.merleg = await this.kGet(`/merleg/${u}?ev=${ev}`);
      } else if (this.ful === 'eszkoz') {
        this.eszkozAdat = await this.nn('GET', `/eszkoz-naplo/${u}?ev=${ev}`);
      } else if (this.ful === 'afa') {
        if (this.afaIdoszak) {
          const [tol, ig] = this.afaTartomany();
          const p = [`tol=${tol}`, `ig=${ig}`, ...(['irany', 'kulcs'] as const).filter((k) => this.afaSzuro[k]).map((k) => `${k}=${encodeURIComponent(this.afaSzuro[k])}`)];
          //  Az arányosítás-sáv a kiválasztott hónapra / negyedévre, az analitikával párhuzamosan.
          const [afa] = await Promise.all([this.kGet(`/afa-analitika/${u}?ev=${ev}&${p.join('&')}`), this.aranyositasBetolt()]);
          this.afa = afa;
          if (this.afa?.afa_gyakorisag) this.afaGyakorisag = this.afa.afa_gyakorisag;
        } else { this.afa = null; this.aranyositas = null; }
      }
      await this.jegyzetekBetolt();
      this.epit();
    } catch (e: any) {
      this.hiba = e?.error?.error || 'Nem sikerült betölteni a naplót.';
      this.oszlopok = []; this.sorok = [];
    } finally {
      this.betoltes = false;
    }
  }

  private async jegyzetekBetolt() {
    try {
      const l: any[] = await this.nn<any[]>('GET', `/jegyzetek/${this.aktiv.ugyfelId}`);
      this.jegyzetek = new Map(l.map((j) => [`${j.forras}|${j.ref_id}`, j.szoveg]));
    } catch { this.jegyzetek = new Map(); }
  }

  /** A forrás-választó elemei a Pénztár/Bank naplóhoz. */
  get forrasok(): { id: string; nev: string }[] {
    const l = this.bankSzamlak.map((b) => ({ id: b.id, nev: `${b.bank_nev || 'Bank'} – ${b.iban}` }));
    //  Egyszeresben a pénztár a Házipénztár fülön van, ezért ide csak a bankszámlák kerülnek;
    //  kettősben a pénztár-számla kartonja is választható forrás.
    if (this.kettos) for (const b of this.bankFokSzamlak) l.push({ id: 'fk:' + b.szamlaszam, nev: `${b.szamlaszam} – ${b.nev || 'Bankszámla'}` });
    if (this.kettos && this.hazipenztarSzamla) l.push({ id: 'penztar', nev: `Pénztár (${this.hazipenztarSzamla})` });
    return l;
  }
  /** A Bank nézetben bankkivonat-forrás van kiválasztva (nem pénztár / nem a karton alapú bankszámla). */
  get kivonatForras(): boolean { return this.ful === 'bank' && this.szuro.forras !== 'penztar' && !String(this.szuro.forras || '').startsWith('fk:'); }
  /** Kettősnél a kartonból listázott forrás főkönyvi száma: a pénztár, vagy a banki kapcsolat nélküli bankszámla. */
  private kartonForras(f: string): string {
    if (f === 'penztar') return this.hazipenztarSzamla;
    return f?.startsWith('fk:') ? f.slice(3) : '';
  }
  /** Forrásváltás a Pénztár/Bank naplóban (kettősnél a pénztár a karton – újra kell tölteni). */
  forrasValt(id: string) {
    this.szuro.forras = id;
    if (this.kettos && this.kartonForras(id)) this.betolt(); else this.epit();
  }

  /* ================= a rácsok felépítése ================= */

  epit() {
    const elozo = this.kijelolt?._kulcs;
    if (this.ful === 'bank') this.epitBank();
    else if (this.ful === 'penzugyi') this.epitPenzugyi();
    else if (this.ful === 'naplofokonyv') this.epitNaplofokonyv();
    else if (this.ful === 'kivonat') this.epitKivonat();
    else if (['vegyes', 'nyito', 'zaro', 'osszes'].includes(this.ful)) this.epitBizonylatok();
    else if (this.ful === 'karton') this.epitKarton();
    else if (this.ful === 'hazipenztar') { if (this.kettos) this.epitKarton(true); else this.epitHazipenztarEgyszeres(); }
    else if (this.ful === 'gyorsmerleg') this.epitGyorsmerleg();
    else if (this.ful === 'afa') this.epitAfa();
    else if (this.ful === 'eszkoz') { if (this.eszkozNezet === 'tabla') this.epitEszkozTabla(); else this.epitEszkozNaplo(); }
    if (!['kivonat', 'gyorsmerleg', 'afa', 'karton'].includes(this.ful) && !(this.ful === 'hazipenztar' && this.kettos)
        && !(this.ful === 'eszkoz' && this.eszkozNezet === 'tabla')) {
      this.sorok = this.sorok.filter((r: any) => r._gyujto || r._fejlec || this.listaIllik(r));
    }
    this.kijelolt = elozo ? this.sorok.find((r) => r._kulcs === elozo) || null : null;
  }

  private nsz(tipus: string, sorszam: any): string {
    if (!sorszam) return '';
    return `${NAPLO_BETU[tipus] || 'V'}${String(this.aktiv.ev).slice(-2)}/${String(sorszam).padStart(6, '0')}`;
  }
  private jel(forras: string, id: any): string { return this.jegyzetek.has(`${forras}|${id}`) ? '📝' : ''; }

  /*  A bankszámlák főkönyvi számának nyitó tételei. A számla főkönyvi száma a bankszámla
   *  beállításából jön; ha nincs megadva és csak EGY bankszámla van, a könyvelési beállítás
   *  bankszámlája (384) a tartalék – több bankszámlánál nem találgatunk. */
  private async bankNyitokBetolt(u: string, ev: number) {
    const tartalek = this.bankSzamlak.length === 1 ? String(this.kettosBeall?.bank_szamla || '').trim() : '';
    const parok = this.bankSzamlak
      .map((b) => ({ bank_id: b.id, fksz: String(b.fokonyvi_szam || '').trim() || tartalek }))
      .filter((x) => x.fksz);
    this.bankNyitok = await Promise.all(parok.map(async (x) => {
      const k: any = await this.kGet(`/fokonyvi-karton/${u}?ev=${ev}&szamla=${encodeURIComponent(x.fksz)}`).catch(() => null);
      //  A kivonathoz NEM kötött könyvelések is (kézi banki bizonylat, pl. előleg; vegyes átvezetés) – a kivonat
      //  sorai mellett ezek is a bank naplójába tartoznak, különben „egyik naplóban sem” látszanak.
      const kotott = new Set(this.bankTetelek.map((t) => String(t.tetel_id || '')).filter(Boolean));
      const sorok = k?.sorok || [];
      return { ...x, sorok: sorok.filter((r: any) => r.naplo_tipus === 'nyito'),
        kezi: sorok.filter((r: any) => r.naplo_tipus !== 'nyito' && !kotott.has(String(r.tetel_id))) };
    }));
  }

  /*  PÉNZTÁR/BANK NAPLÓ. A nyitó és a záró egyenleg a listában szereplő tételekből
   *  göngyölődik, számlánként, időrendben: a bank a szinkronnal nem közli a számla
   *  egyenlegét, ezért az első szinkronizált tétel előtti egyenleg 0-nak számít. */
  private epitBank() {
    const nev = this.negativ;
    this.oszlopok = [
      { k: 'jel', c: '' },
      { k: 'nsz', c: 'Naplósorszám' }, { k: 'fksz', c: 'Fők.sz.' }, { k: 'datum', c: 'Dátum' },
      ...(this.kettos ? [{ k: 'biz', c: 'Biz.szám' }] : this.szuro.forras === 'penztar' ? [{ k: 'biz', c: 'Biz.szám' }] : []),
      { k: 'hiv', c: 'Hivatkozás', cim: 'A kiegyenlített számla' },
      { k: 'partner', c: 'Partner neve', szeles: true }, { k: 'megj', c: 'Megjegyzés', szeles: true }, { k: 'bk', c: 'B/K' },
      { k: 'osszeg', c: 'Összeg', num: true, osszeg: true },
      { k: 'nyito', c: 'Nyitó egy.', num: true, cs: 'cs-penz' }, { k: 'zaro', c: 'Záró egy.', num: true, cs: 'cs-penz' },
      { k: 'dev', c: 'Dev. érték', num: true, cs: 'cs-deviza' }, { k: 'devnem', c: 'Dev.nem', cs: 'cs-deviza' },
      { k: 'ellen', c: 'Ellenszámla' }, { k: 'statusz', c: 'Státusz' },
    ];
    const f = this.szuro.forras;
    const sorok: any[] = [];
    if (this.kettos && this.kartonForras(f)) {
      // Kettős: a pénztár-számla (vagy a banki kapcsolat nélküli bankszámla) kartonja (T = bevétel, K = kiadás),
      // a bizonylatok naplósorszámával.
      const fksz = this.kartonForras(f);
      let egy = 0;
      for (const r of (this.karton?.sorok || [])) {
        const osszeg = (Number(r.tartozik) || 0) - (Number(r.kovetel) || 0);
        const nyito = egy; egy += osszeg;
        sorok.push({
          jel: this.jel('kettos', r.tetel_id),
          nsz: this.nsz(r.naplo_tipus, r.sorszam), fksz, datum: String(r.datum || '').slice(0, 10), biz: r.bizonylatszam || '',
          hiv: '', partner: r.partner_nev || '', megj: r.megjegyzes || r.megnevezes || '', bk: osszeg >= 0 ? 'bevétel' : 'kiadás',
          osszeg: nev ? osszeg : Math.abs(osszeg), nyito, zaro: egy, dev: null, devnem: '', ellen: '', statusz: 'könyvelt',
          _kulcs: 'kp' + r.tetel_id + r.sorszam, _fajta: 'kettos', _id: r.tetel_id, _naplo: r.naplo_tipus, _partner: r.partner_nev,
        });
      }
    } else if (f === 'penztar') {
      const tetelek = (this.nfk?.sorok || []).filter((r: any) => r.penztar_tipus === 'penztar');
      let egy = 0; let i = 0;
      for (const r of tetelek) {
        const osszeg = (Number(r.bevetel) || 0) - (Number(r.kiadas) || 0);
        const nyito = egy; egy += osszeg; i++;
        sorok.push({
          jel: this.jel('egyszeres', r.id),
          nsz: `P/${String(i).padStart(6, '0')}`, fksz: r.penztar_azon || '', datum: r.datum, biz: r.bizonylatszam || '',
          hiv: '', partner: r.partner_nev || '', megj: r.megjegyzes || '', bk: osszeg >= 0 ? 'bevétel' : 'kiadás',
          osszeg: nev ? osszeg : Math.abs(osszeg), nyito, zaro: egy, dev: null, devnem: '', ellen: '', statusz: 'könyvelt',
          _kulcs: 'e' + r.id, _fajta: 'egyszeres', _id: r.id, _partner: r.partner_nev, _osa: r.osa_szamlaszam, _osaIrany: r.osa_irany, _kep: !!Number(r.van_kep),
        });
      }
    } else {
      const szamla = this.bankSzamlak.find((b) => b.id === f);
      const lista = this.bankTetelek
        .filter((t) => !f || t.bank_id === f)
        .sort((a, b) => String(a.datum).localeCompare(String(b.datum)) || String(a.letrehozva).localeCompare(String(b.letrehozva)));
      let egy = 0; let i = 0;
      //  Kettősnél a főkönyvi nyitó tétel(ek) az első sor(ok): innen göngyölődik a bank egyenlege.
      for (const bn of this.bankNyitok.filter((x) => !f || x.bank_id === f)) {
        for (const r of bn.sorok) {
          const osszeg = (Number(r.tartozik) || 0) - (Number(r.kovetel) || 0);
          const nyito = egy; egy += osszeg;
          sorok.push({
            jel: this.jel('kettos', r.tetel_id),
            nsz: this.nsz(r.naplo_tipus, r.sorszam), fksz: bn.fksz, datum: String(r.datum || '').slice(0, 10), biz: r.bizonylatszam || '',
            hiv: '', partner: '', megj: r.megjegyzes || r.megnevezes || 'Nyitó egyenleg', bk: 'nyitó',
            osszeg: nev ? osszeg : Math.abs(osszeg), nyito, zaro: egy, dev: null, devnem: '', ellen: '', statusz: 'könyvelt',
            _kulcs: 'bn' + r.tetel_id + bn.fksz, _fajta: 'kettos', _id: r.tetel_id, _naplo: r.naplo_tipus,
          });
        }
      }
      //  A kivonat tételei és a kivonathoz nem kötött kézi banki könyvelések (pl. előleg) időrendben, közös egyenleggel.
      const kezi = this.bankNyitok.filter((x) => !f || x.bank_id === f).flatMap((bn) => bn.kezi.map((r: any) => ({ r, fksz: bn.fksz })));
      const egyutt: any[] = [...lista.map((t) => ({ datum: String(t.datum || '').slice(0, 10), t })), ...kezi.map((k) => ({ datum: String(k.r.datum || '').slice(0, 10), k }))]
        .sort((a, b) => a.datum.localeCompare(b.datum));
      for (const e of egyutt) {
        if (e.k) {
          const { r, fksz } = e.k;
          const osszeg = (Number(r.tartozik) || 0) - (Number(r.kovetel) || 0);
          const nyito = egy; egy += osszeg;
          sorok.push({
            jel: this.jel('kettos', r.tetel_id),
            nsz: this.nsz(r.naplo_tipus, r.sorszam), fksz, datum: e.datum, biz: r.bizonylatszam || '',
            hiv: '', partner: r.partner_nev || '', megj: r.megjegyzes || r.megnevezes || '', bk: osszeg >= 0 ? 'bevétel' : 'kiadás',
            osszeg: nev ? osszeg : Math.abs(osszeg), nyito, zaro: egy, dev: null, devnem: '', ellen: '', statusz: 'könyvelt',
            _kulcs: 'bk' + r.tetel_id + (r.sorszam || '') + fksz, _fajta: 'kettos', _id: r.tetel_id, _naplo: r.naplo_tipus, _partner: r.partner_nev,
          });
          continue;
        }
        const t = e.t;
        const osszeg = Number(t.osszeg) || 0;
        const nyito = egy; egy += osszeg; i++;
        sorok.push({
          jel: this.jel('bank', t.id),
          nsz: `B/${String(i).padStart(6, '0')}`, fksz: t.bank_fokonyvi || szamla?.fokonyvi_szam || '',
          datum: String(t.datum || '').slice(0, 10), biz: '', hiv: this.hivatkozas(t),
          partner: t.partner_felulir || t.partner_nev || '', megj: t.kozlemeny || '', bk: osszeg >= 0 ? 'bevétel' : 'kiadás',
          osszeg: nev ? osszeg : Math.abs(osszeg), nyito, zaro: egy,
          dev: t.deviza_nem && t.deviza_nem !== 'HUF' ? Number(t.deviza_osszeg) || null : null,
          devnem: t.deviza_nem && t.deviza_nem !== 'HUF' ? t.deviza_nem : '',
          ellen: t.fokonyvi_szam || '', statusz: t.statusz || '',
          _kulcs: 'b' + t.id, _fajta: 'bank', _id: t.id, _tetel: t.tetel_id || null, _statusz: t.statusz, _osszegNyers: osszeg,
          _partner: t.partner_felulir || t.partner_nev || '', _paroztt: this.hivatkozas(t),
        });
      }
    }
    const { tol, ig, statusz } = this.szuro;
    this.sorok = sorok.filter((r) => (!tol || r.datum >= tol) && (!ig || r.datum <= ig) && (!statusz || r.statusz === statusz));
  }

  private hivatkozas(t: any): string {
    let p: any = t.parositas;
    if (!p) return '';
    if (typeof p === 'string') { try { p = JSON.parse(p); } catch { return ''; } }
    const bizok = (p?.tetelek || []).map((x: any) => x.bizonylatszam).filter(Boolean);
    return (bizok.length ? bizok : [p?.bizonylatszam].filter(Boolean)).join(', ');
  }

  /** ÁFA-analitika: '' = még nem választott (a választó látszik), 'ev' = teljes év, 'Q1'…'Q4' = negyedév,
   *  '01'…'12' = hónap. Ugyanaz a kódolás, mint a Kettős könyvelés ÁFA-analitikájában (kettos.component). */
  afaIdoszak = '';
  afaSzuro: { irany: string; kulcs: string } = { irany: '', kulcs: '' };
  readonly afaHonapok = ['Január', 'Február', 'Március', 'Április', 'Május', 'Június', 'Július', 'Augusztus', 'Szeptember', 'Október', 'November', 'December'];
  readonly afaNegyedek = ['I. negyedév', 'II. negyedév', 'III. negyedév', 'IV. negyedév'];
  /*  Az ügyfél ÁFA-bevallási gyakorisága (havi | negyedeves | eves; backend: GET /kettos/afa-gyakorisag).
   *  A Naplókban (a Kettős nézettel ellentétben) a negyedév ÉS a hónap is mindig választható – ez
   *  áttekintő lista, a havi bontás negyedéves bevallónál is kell (2026-11-02); a gyakoriság csak azt
   *  dönti el, melyik a bevallási időszak (azt jelöljük, és az arányosítás csak arra könyvelhető). */
  afaGyakorisag = 'negyedeves';
  get afaNegyedeves(): boolean { return this.afaGyakorisag === 'negyedeves'; }
  get afaHavi(): boolean { return this.afaGyakorisag === 'havi'; }
  get afaGyakorisagSzoveg(): string { return ({ havi: 'havi', negyedeves: 'negyedéves', eves: 'éves' } as any)[this.afaGyakorisag] || ''; }
  private async afaGyakorisagBetolt() {
    try { const r: any = await this.kGet(`/afa-gyakorisag/${this.aktiv.ugyfelId}`); this.afaGyakorisag = r?.afa_gyakorisag || 'negyedeves'; }
    catch { this.afaGyakorisag = 'negyedeves'; }
  }
  afaHoKod(i: number) { return String(i + 1).padStart(2, '0'); }
  afaNegyedKod(i: number) { return 'Q' + (i + 1); }
  afaIdoszakValaszt(v: string) { this.afaIdoszak = v; this.betolt(); }
  /** A kiválasztott időszak olvasható neve (nyomtatás, arányosítás-sáv). */
  get afaIdoszakCimke(): string {
    const v = this.afaIdoszak;
    if (!v) return '';
    if (v === 'ev') return 'Teljes év';
    if (v[0] === 'Q') return this.afaNegyedek[Number(v.slice(1)) - 1] || v;
    return this.afaHonapok[Number(v) - 1] || v;
  }
  private afaTartomany(): [string, string] {
    const ev = this.aktiv.ev, v = this.afaIdoszak;
    if (v === 'ev') return [`${ev}-01-01`, `${ev}-12-31`];
    const ket = (n: number) => String(n).padStart(2, '0');
    if (v[0] === 'Q') {
      const kezd = (Number(v.slice(1)) - 1) * 3 + 1, utolso = new Date(Number(ev), kezd + 2, 0).getDate();
      return [`${ev}-${ket(kezd)}-01`, `${ev}-${ket(kezd + 2)}-${ket(utolso)}`];
    }
    const utolso = new Date(Number(ev), Number(v), 0).getDate();
    return [`${ev}-${v}-01`, `${ev}-${v}-${ket(utolso)}`];
  }

  /* ── ÁFA-ARÁNYOSÍTÁS a Naplók ÁFA-analitikájában ──
   *  Ugyanaz a backend-számítás, mint a Kettős könyvelés ÁFA-nézetében (kettos.js aranyositasSzamol:
   *  GET/POST /kettos/afa-aranyositas) – itt nincs saját képlet. Csak hónapra / negyedévre, és csak ha
   *  az ügyfélnél van mit arányosítani (van adómentes bevétel, vagy már készült arányosítás-bizonylat):
   *  enélkül a levonási hányad 1, a sáv csak nullákat mutatna. KÖNYVELNI csak a bevallási időszakra
   *  lehet (negyedéves bevallónál negyedévre, havinál hónapra) – a bizonylatszám az időszakhoz kötött
   *  (ARANYOSITAS/<év>-Qn vagy <év>-hh), a másik bontásban könyvelve ugyanaz a rész kétszer kerülne le. */
  aranyositas: any = null;
  aranyositasSor = '68';
  aranyositasFut = false;
  get aranyositasIdoszak(): boolean { return !!this.afaIdoszak && this.afaIdoszak !== 'ev'; }
  get aranyositasLathato(): boolean {
    const a = this.aranyositas;
    return this.ful === 'afa' && this.aranyositasIdoszak && !!a && (Number(a.mentes_netto) !== 0 || !!a.mar_konyvelt);
  }
  get aranyositasKonyvelheto(): boolean {
    const v = this.afaIdoszak;
    return (v[0] === 'Q' && this.afaNegyedeves) || (/^\d\d$/.test(v) && this.afaHavi);
  }
  private aranyParam(): { negyedev?: number; honap?: number } {
    const v = this.afaIdoszak;
    if (v && v[0] === 'Q') return { negyedev: Number(v.slice(1)) };
    if (v && v !== 'ev') return { honap: Number(v) };
    return {};
  }
  async aranyositasBetolt() {
    this.aranyositas = null;
    if (!this.aranyositasIdoszak || !this.aktiv.ugyfelId) return;
    const p = this.aranyParam();
    const qs = p.negyedev ? `&negyedev=${p.negyedev}` : `&honap=${p.honap}`;
    this.aranyositas = await this.kGet(`/afa-aranyositas/${this.aktiv.ugyfelId}?ev=${this.aktiv.ev}${qs}&sor=${encodeURIComponent(this.aranyositasSor)}`).catch(() => null);
  }
  async aranyositasKonyvel() {
    const a = this.aranyositas;
    if (!a || !this.aranyositasKonyvelheto) return;
    if (a.le_nem_vonhato <= 0) { this.uzen('Nincs le nem vonható rész (0 Ft) ebben az időszakban.'); return; }
    const ok = await this.megerosit('ÁFA-arányosítás könyvelése',
      `${a.idoszak} – le nem vonható előzetes ÁFA: ${this.szam(a.le_nem_vonhato)} Ft (levonási hányad ${a.levonasi_hanyad}, a ${a.sor}. sor áfatartalma ${this.szam(a.afa_68)} Ft). Könyvelés: T ${a.le_nem_vonhato_szamla} / K ${a.afa_szamla} az időszak utolsó napjára.${a.mar_konyvelt ? ' (A meglévő arányosítás-bizonylat frissül.)' : ''}`, 'Könyvelés');
    if (!ok) return;
    this.aranyositasFut = true;
    try {
      const r: any = await firstValueFrom(this.http.post(`/api/kettos/afa-aranyositas/${this.aktiv.ugyfelId}?ev=${this.aktiv.ev}`, { ...this.aranyParam(), sor: this.aranyositasSor }));
      this.uzen(r?.konyvelve ? `Arányosítás könyvelve: ${this.szam(r.le_nem_vonhato)} Ft (${r.bizonylatszam}).` : (r?.uzenet || 'Nincs mit könyvelni.'));
      await this.betolt();   // a 466-os előzetes ÁFA változott: az analitika és a sáv is frissül
    } catch (e: any) { this.uzen(e?.error?.error || 'Az arányosítás könyvelése nem sikerült.', true); }
    finally { this.aranyositasFut = false; }
  }

  pnNezet: 'vegyes' | 'csoport' = 'vegyes';
  pnNezetValt(n: 'vegyes' | 'csoport') { this.pnNezet = n; this.epitPenzugyi(); }
  /** A számla napló-sorszáma: vevő K26/000001, szállító S26/000001, a nyitóra kötött áthozott N26/…. */
  private pnSorszam(r: any): string {
    const [naplo, ev, szam] = String(r.tetel_sorszam || '').split(':');
    if (!naplo || !szam) return '';
    return `${NAPLO_BETU[naplo] || 'V'}${String(ev).slice(-2)}/${String(szam).padStart(6, '0')}`;
  }

  /*  PÉNZÜGYI NYILVÁNTARTÁS: vevő- és szállítószámlák. Devizás számlánál a végösszeg a
   *  számla pénznemében áll (a forintérték a kiegyenlítéskor, árfolyammal dől el), ezért az
   *  a deviza-oszlopokba kerül. */
  private epitPenzugyi() {
    const ma = new Date().toISOString().slice(0, 10);
    this.oszlopok = [
      { k: 'jel', c: '' },
      { k: 'lejar', c: 'Lejár' }, { k: 'sorszam', c: 'Sorszám' }, { k: 'tipus', c: 'Típus' }, { k: 'kelt', c: 'Kelt' }, { k: 'telj', c: 'Telj.' },
      { k: 'afaesed', c: 'Áfa esed.' }, { k: 'hat', c: 'Fiz.hat.' }, { k: 'biz', c: 'Bizonylatszám' },
      { k: 'partner', c: 'Partner neve', szeles: true }, { k: 'megj', c: 'Megjegyzés', szeles: true },
      { k: 'vegosszeg', c: 'Végösszeg', num: true, osszeg: true }, { k: 'rendezve', c: 'Rendezve', num: true, osszeg: true },
      { k: 'hatralek', c: 'Hátralék', num: true, osszeg: true },
      { k: 'devveg', c: 'Dev. végösszeg', num: true, cs: 'cs-deviza' }, { k: 'devrend', c: 'Dev. rendezve', num: true, cs: 'cs-deviza' },
      { k: 'devnem', c: 'Dev.nem', cs: 'cs-deviza' },
    ];
    const elojel = (r: any) => (this.negativ && r.tipus === 'szallito' ? -1 : 1);
    const sorok = (this.kiegy || []).map((r: any) => {
      const deviza = r.deviza && r.deviza !== 'HUF' ? r.deviza : '';
      const brutto = Number(r.brutto) || 0, rend = Number(r.kiegyenlitett) || 0;
      const nyitott = rend + 0.5 < brutto;
      const tulfizetett = rend > brutto + 0.5;
      const lejart = nyitott && r.fizetesi_hatarido && r.fizetesi_hatarido < ma;
      const e = elojel(r);
      return {
        jel: this.jel('kiegy', r.id),
        sorszam: this.pnSorszam(r),
        lejar: tulfizetett ? 'túlfiz.' : !nyitott ? 'rend.' : (lejart ? 'lejárt' : ''), tipus: r.tipus === 'szallito' ? 'Szállító' : 'Vevő',
        kelt: r.kelt || '', telj: r.teljesites || '', afaesed: r.afa_esedekesseg || '', hat: r.fizetesi_hatarido || '',
        biz: r.bizonylatszam || '', partner: r.partner_nev || '', megj: r.megjegyzes || '',
        vegosszeg: deviza ? null : e * brutto, rendezve: deviza ? null : e * rend, hatralek: deviza ? null : e * (brutto - rend),
        devveg: deviza ? e * brutto : null, devrend: deviza ? e * rend : null, devnem: deviza,
        _kulcs: 'k' + r.id, _fajta: 'kiegy', _id: r.id, _nyers: r, _partner: r.partner_nev,
        _tipus: r.tipus, _nyitott: nyitott, _lejart: lejart, _hatralekHuf: deviza ? 0 : brutto - rend,
        //  TAVALYRÓL ÁTHOZOTT számla: a kelte korábbi évre esik, mint a nyilvántartásé. Csak itt, a
        //  pénzügyi nyilvántartásban él (a vevő/szállító naplóban nincs), ezért külön színnel jelöljük.
        _tulfizetett: tulfizetett,
        _athozott: !!r.kelt && Number(String(r.kelt).slice(0, 4)) < Number(this.aktiv.ev),
      };
    });
    const { tol, ig, tipus, allapot } = this.szuro;
    const szurt = sorok.filter((r: any) => (!tol || r.kelt >= tol) && (!ig || r.kelt <= ig)
      && (!tipus || r._tipus === tipus)
      && (!allapot || (allapot === 'nyitott' ? r._nyitott : allapot === 'lejart' ? r._lejart : !r._nyitott)));
    //  KÉT NÉZET, mindkettő a KELTEZÉS szerint: „vegyes” – vevő és szállító együtt; „vevők, majd
    //  szállítók” – típusonként, hogy a két nyilvántartás (külön sorszámmal) külön is olvasható legyen.
    const k = (r: any) => `${this.pnNezet === 'csoport' ? (r._tipus === 'vevo' ? 0 : 1) : ''}|${r.kelt || ''}|${r.sorszam || ''}|${r.biz || ''}`;
    szurt.sort((a: any, b: any) => k(a).localeCompare(k(b)));
    this.sorok = szurt;
  }

  /*  NAPLÓFŐKÖNYV (egyszeres): pénztár és bank oszlopcsoport göngyölt egyenleggel, a hét
   *  fő oszlop, és a fizetendő / levonható ÁFA külön. */
  private epitNaplofokonyv() {
    const fo = (this.nfk?.fooszlopok || []) as any[];
    const rovid: Record<number, string> = { 1: 'Bevételek', 2: 'Er.n.sz.bev.', 3: 'Anyag, áru', 4: 'Munkabér', 5: 'M.bér közter.', 6: 'Egyéb költség', 7: 'E.n.sz.kiad.' };
    this.oszlopok = [
      { k: 'jel', c: '' },
      { k: 'ssz', c: 'Ssz', num: true }, { k: 'kelt', c: 'Kelt' }, { k: 'biz', c: 'Bizonylatszám' }, { k: 'partner', c: 'Partner' },
      { k: 'szoveg', c: 'Szöveg', szeles: true },
      { k: 'pbev', c: 'Pénztár bev.', num: true, cs: 'cs-penz', osszeg: true }, { k: 'pkiad', c: 'Pénztár kiad.', num: true, cs: 'cs-penz', osszeg: true },
      { k: 'pegy', c: 'Pénztár egy.', num: true, cs: 'cs-penz' },
      { k: 'bbev', c: 'Bank bev.', num: true, cs: 'cs-penz', osszeg: true }, { k: 'bkiad', c: 'Bank kiad.', num: true, cs: 'cs-penz', osszeg: true },
      { k: 'begy', c: 'Bank egy.', num: true, cs: 'cs-penz' },
      ...[1, 2].map((i) => ({ k: 'o' + i, c: rovid[i], num: true, osszeg: true, cim: fo.find((x) => x.oszlop === i)?.nev })),
      { k: 'fizafa', c: 'Fiz. ÁFA', num: true, cs: 'cs-afa' as const, osszeg: true },
      ...[3, 4, 5, 6, 7].map((i) => ({ k: 'o' + i, c: rovid[i], num: true, osszeg: true, cim: fo.find((x) => x.oszlop === i)?.nev })),
      { k: 'levafa', c: 'Levonh. ÁFA', num: true, cs: 'cs-afa' as const, osszeg: true },
    ];
    let pegy = 0, begy = 0;
    const sorok = (this.nfk?.sorok || []).map((r: any) => {
      const bev = Number(r.bevetel) || 0, kiad = Number(r.kiadas) || 0;
      const bank = r.penztar_tipus === 'bank';
      if (bank) begy += bev - kiad; else pegy += bev - kiad;
      const afa = (irany: string) => (r.sorok || []).filter((s: any) => s.irany === irany).reduce((a: number, s: any) => a + (Number(s.afa) || 0), 0);
      const sor: any = {
        jel: this.jel('egyszeres', r.id),
        ssz: r.sorszam, kelt: r.datum, biz: r.bizonylatszam || '', partner: r.partner_nev || '', szoveg: r.megjegyzes || '',
        pbev: !bank && bev ? bev : null, pkiad: !bank && kiad ? kiad : null, pegy: bank ? null : pegy,
        bbev: bank && bev ? bev : null, bkiad: bank && kiad ? kiad : null, begy: bank ? begy : null,
        fizafa: afa('bevetel') || null, levafa: afa('kiadas') || null,
        _kulcs: 'e' + r.id, _fajta: 'egyszeres', _id: r.id, _partner: r.partner_nev, _osa: r.osa_szamlaszam, _osaIrany: r.osa_irany, _kep: !!Number(r.van_kep),
      };
      for (let i = 1; i <= 7; i++) sor['o' + i] = Number(r.oszlopok?.[i]) || null;
      return sor;
    });
    const { tol, ig } = this.szuro;
    this.sorok = sorok.filter((r: any) => (!tol || r.kelt >= tol) && (!ig || r.kelt <= ig));
  }

  /** HÁZIPÉNZTÁR egyszeres könyvelésnél: a pénztári tételek bevétel/kiadás/egyenleg oszlopokkal. */
  private epitHazipenztarEgyszeres() {
    this.oszlopok = [
      { k: 'jel', c: '' }, { k: 'ssz', c: 'Ssz', num: true }, { k: 'datum', c: 'Dátum' }, { k: 'biz', c: 'Bizonylatszám' },
      { k: 'partner', c: 'Partner', szeles: true }, { k: 'szoveg', c: 'Szöveg', szeles: true },
      { k: 'bev', c: 'Bevétel', num: true, cs: 'cs-penz', osszeg: true }, { k: 'kiad', c: 'Kiadás', num: true, cs: 'cs-penz', osszeg: true },
      { k: 'egy', c: 'Egyenleg', num: true, cs: 'cs-penz' }, { k: 'penztar', c: 'Pénztár' },
    ];
    let egy = 0;
    const sorok = (this.nfk?.sorok || []).filter((r: any) => r.penztar_tipus === 'penztar').map((r: any) => {
      const bev = Number(r.bevetel) || 0, kiad = Number(r.kiadas) || 0;
      egy += bev - kiad;
      return {
        jel: this.jel('egyszeres', r.id), ssz: r.sorszam, datum: r.datum, biz: r.bizonylatszam || '', partner: r.partner_nev || '',
        szoveg: r.megjegyzes || '', bev: bev || null, kiad: kiad || null, egy, penztar: r.penztar_nev || r.penztar_azon || '',
        _kulcs: 'e' + r.id, _fajta: 'egyszeres', _id: r.id, _partner: r.partner_nev, _osa: r.osa_szamlaszam, _osaIrany: r.osa_irany, _kep: !!Number(r.van_kep),
      };
    });
    const { tol, ig } = this.szuro;
    this.sorok = sorok.filter((r: any) => (!tol || r.datum >= tol) && (!ig || r.datum <= ig));
  }

  /*  FŐKÖNYVI KIVONAT (kettős): számlánként nyitó, forgalom, egyenleg; a gyűjtők kiemelve. */
  /*  FŐKÖNYVI KIVONAT. Az oszlopok a szerver által ténylegesen alkalmazott szűrőtől függnek:
   *  alap (Nyitó, forgalom, egyenleg), nyolcoszlopos „előző időszak" nézet, vagy 4 forintos + 4
   *  devizás oszlop. Az alábontott sorok (partner / munkaszám) a számla sora alá kerülnek; az
   *  Összesen csak a könyvelhető számlák sorait adja össze (a gyűjtők és az alábontás nélkül). */
  private epitKivonat() {
    const sz = this.kivonat?.szuro || {};
    const n = (v: any) => Number(v) || null;
    const fej = [{ k: 'szamla', c: 'Számla' }, { k: 'nev', c: 'Megnevezés', szeles: true }];
    let cellak: (r: any) => any;
    if (sz.deviza_ertek) {
      this.oszlopok = [...fej,
        { k: 'ft', c: 'Forgalom T', num: true, cs: 'cs-penz', osszeg: true }, { k: 'fk', c: 'Forgalom K', num: true, cs: 'cs-penz', osszeg: true },
        { k: 'egyt', c: 'Egyenleg T', num: true, osszeg: true }, { k: 'egyk', c: 'Egyenleg K', num: true, osszeg: true },
        { k: 'dev', c: 'Deviza', cs: 'cs-deviza', cim: '*** = több devizanem, nem összegezhető' },
        { k: 'dt', c: 'Dev. forgalom T', num: true, cs: 'cs-deviza' }, { k: 'dk', c: 'Dev. forgalom K', num: true, cs: 'cs-deviza' },
        { k: 'det', c: 'Dev. egyenleg T', num: true, cs: 'cs-deviza' }, { k: 'dek', c: 'Dev. egyenleg K', num: true, cs: 'cs-deviza' }];
      cellak = (r) => ({ ft: n(r.nyito_t + r.tartozik), fk: n(r.nyito_k + r.kovetel), egyt: n(r.egyenleg_t), egyk: n(r.egyenleg_k),
        dev: r.deviza || '', dt: n(r.dev_tartozik), dk: n(r.dev_kovetel),
        det: n(r.dev_tartozik > r.dev_kovetel ? r.dev_tartozik - r.dev_kovetel : 0), dek: n(r.dev_kovetel > r.dev_tartozik ? r.dev_kovetel - r.dev_tartozik : 0) });
    } else if (sz.elozo) {
      this.oszlopok = [...fej,
        { k: 'nyt', c: 'Nyitó T', num: true, osszeg: true }, { k: 'nyk', c: 'Nyitó K', num: true, osszeg: true },
        { k: 'tartozik', c: 'Forgalom T', num: true, cs: 'cs-penz', osszeg: true }, { k: 'kovetel', c: 'Forgalom K', num: true, cs: 'cs-penz', osszeg: true },
        { k: 'fet', c: 'Forg. egyenleg T', num: true, osszeg: true }, { k: 'fek', c: 'Forg. egyenleg K', num: true, osszeg: true },
        { k: 'egyt', c: 'Összesen egy. T', num: true, osszeg: true }, { k: 'egyk', c: 'Összesen egy. K', num: true, osszeg: true }];
      cellak = (r) => ({ nyt: n(r.nyito_t), nyk: n(r.nyito_k), tartozik: n(r.tartozik), kovetel: n(r.kovetel),
        fet: n(r.forg_egyenleg_t), fek: n(r.forg_egyenleg_k), egyt: n(r.egyenleg_t), egyk: n(r.egyenleg_k) });
    } else {
      this.oszlopok = [...fej,
        { k: 'nyito', c: 'Nyitó', num: true }, { k: 'tartozik', c: 'Tartozik forgalom', num: true, cs: 'cs-penz', osszeg: true },
        { k: 'kovetel', c: 'Követel forgalom', num: true, cs: 'cs-penz', osszeg: true },
        { k: 'egyt', c: 'Egyenleg T', num: true, osszeg: true }, { k: 'egyk', c: 'Egyenleg K', num: true, osszeg: true }];
      cellak = (r) => ({ nyito: n(r.nyito), tartozik: n(r.tartozik), kovetel: n(r.kovetel), egyt: n(r.egyenleg_t), egyk: n(r.egyenleg_k) });
    }
    const sorok: any[] = [];
    for (const r of (this.kivonat?.sorok || [])) {
      sorok.push({ szamla: r.szamlaszam, nev: r.nev, ...cellak(r), _gyujto: !!r.gyujto, _resz: !!r.gyujto,
        _kulcs: 'sz' + r.szamlaszam, _fajta: 'szamla', _szamla: String(r.szamlaszam) });
      for (const a of (r.albontas || [])) {
        sorok.push({ szamla: a.cimke, nev: a.nev, ...cellak(a), _alab: true, _resz: true,
          _kulcs: 'sz' + r.szamlaszam + '|' + a.cimke, _fajta: 'szamla', _szamla: String(r.szamlaszam) });
      }
    }
    this.sorok = sorok;
  }

  /*  VEGYES / NYITÓ / ZÁRÓ / ÖSSZES NAPLÓ (kettős): a bizonylatok fejléce. */
  private epitBizonylatok() {
    const osszes = this.ful === 'osszes';
    this.oszlopok = [
      { k: 'jel', c: '' }, { k: 'nsz', c: 'Naplósorszám' }, ...(osszes ? [{ k: 'naplo', c: 'Napló' }] : []),
      { k: 'datum', c: 'Dátum' }, { k: 'biz', c: 'Bizonylatszám' }, { k: 'partner', c: 'Partner', szeles: true },
      { k: 'megnev', c: 'Megnevezés', szeles: true }, { k: 'osszeg', c: 'Összeg', num: true, osszeg: true, cim: 'A bizonylat tartozik-oldali összege' },
      { k: 'deviza', c: 'Dev.', cs: 'cs-deviza' },
    ];
    const sorok = (this.bizonylatok || []).map((t: any) => ({
      jel: this.jel('kettos', t.id), nsz: this.nsz(t.naplo_tipus, t.sorszam), naplo: NAPLO_NEV[t.naplo_tipus] || t.naplo_tipus,
      datum: String(t.datum || '').slice(0, 10), biz: t.bizonylatszam || '', partner: t.partner_nev || '', megnev: t.megnevezes || '',
      osszeg: Number(t.osszeg) || null, deviza: t.deviza || '',
      _szlak: String(t.szamlak || '').split(',').filter(Boolean), _msz: String(t.munkaszamok || '').split(',').filter(Boolean),
      _kulcs: 't' + t.id, _fajta: 'kettos', _id: t.id, _naplo: t.naplo_tipus, _partner: t.partner_nev,
      _kep: !!t.kep_nev, _osa: t.osa_szamlaszam, _osaIrany: t.osa_irany,
    }));
    const { tol, ig } = this.szuro;
    this.sorok = sorok.filter((r: any) => (!tol || r.datum >= tol) && (!ig || r.datum <= ig));
  }

  /*  FŐKÖNYVI KARTON (és a kettős HÁZIPÉNZTÁR: a pénztár-számla kartonja). */
  private epitKarton(penztar = false) {
    this.oszlopok = [
      { k: 'jel', c: '' }, { k: 'datum', c: 'Dátum' }, { k: 'nsz', c: 'Naplósorszám' }, { k: 'biz', c: 'Bizonylatszám' },
      { k: 'partner', c: 'Partner', szeles: true }, { k: 'megnev', c: 'Megnevezés', szeles: true },
      { k: 't', c: penztar ? 'Bevétel' : 'Tartozik', num: true, cs: 'cs-penz', osszeg: true },
      { k: 'k', c: penztar ? 'Kiadás' : 'Követel', num: true, cs: 'cs-penz', osszeg: true },
      { k: 'egy', c: 'Egyenleg', num: true },
    ];
    const { tol, ig } = this.szuro;
    const tetelek = (this.karton?.sorok || []).map((r: any, i: number) => ({
      jel: this.jel('kettos', r.tetel_id), datum: String(r.datum || '').slice(0, 10), nsz: this.nsz(r.naplo_tipus, r.sorszam),
      biz: r.bizonylatszam || '', partner: r.partner_nev || '', megnev: r.megjegyzes || r.megnevezes || '',
      t: Number(r.tartozik) || null, k: Number(r.kovetel) || null, egy: Number(r.egyenleg) || 0,
      _kulcs: 'kr' + i + (r.tetel_id || ''), _fajta: 'kettos', _id: r.tetel_id, _naplo: r.naplo_tipus, _partner: r.partner_nev,
      _szla: String(r.szamlaszam || this.karton?.szamla || ''), _szlaNev: r.nev || this.karton?.nev || '',
      _msz: r.munkaszam ? [String(r.munkaszam)] : [], _dev: r.deviza || '',
    })).filter((r: any) => (!tol || r.datum >= tol) && (!ig || r.datum <= ig) && this.listaIllik(r));
    if (penztar || this.szuro.szamla !== '*') { this.sorok = tetelek; return; }
    // Minden számla: számlánként fejléc, a tételek göngyölt egyenleggel, és részösszeg.
    const sorok: any[] = [];
    let i = 0;
    while (i < tetelek.length) {
      const szla = tetelek[i]._szla;
      const csop: any[] = [];
      while (i < tetelek.length && tetelek[i]._szla === szla) csop.push(tetelek[i++]);
      sorok.push({ megnev: `${szla} – ${csop[0]._szlaNev || ''}`, _gyujto: true, _fejlec: true, _kulcs: 'kf' + szla, _fajta: 'szamla', _szamla: szla });
      sorok.push(...csop);
      const t = csop.reduce((a, r) => a + (r.t || 0), 0), k = csop.reduce((a, r) => a + (r.k || 0), 0);
      sorok.push({ megnev: `${szla} összesen`, t: t || null, k: k || null, egy: Math.round((t - k) * 100) / 100, _gyujto: true, _resz: true, _kulcs: 'ko' + szla, _fajta: 'szamla', _szamla: szla });
    }
    this.sorok = sorok;
  }
  get hazipenztarSzamla(): string { return this.kettosBeall?.penztar_szamla || ''; }

  /*  GYORSMÉRLEG (kettős): eszközök és források számlánként, összesítő sorokkal. */
  private epitGyorsmerleg() {
    this.oszlopok = [
      { k: 'oldal', c: 'Oldal' }, { k: 'szamla', c: 'Számla' }, { k: 'nev', c: 'Megnevezés', szeles: true }, { k: 'osszeg', c: 'Összeg', num: true },
    ];
    const m = this.merleg || {};
    const sor = (oldal: string, r: any) => ({ oldal, szamla: r.szamlaszam, nev: r.nev, osszeg: Number(r.osszeg) || 0, _kulcs: 'm' + oldal + r.szamlaszam, _fajta: 'szamla', _szamla: String(r.szamlaszam) });
    this.sorok = [
      ...(m.eszkozok || []).map((r: any) => sor('Eszközök', r)),
      { oldal: 'Eszközök', nev: 'ESZKÖZÖK ÖSSZESEN', osszeg: Number(m.eszkoz_ossz) || 0, _gyujto: true, _kulcs: 'm-eo' },
      ...(m.forrasok || []).map((r: any) => sor('Források', r)),
      { oldal: 'Források', nev: 'Mérleg szerinti eredmény (MSZE)', osszeg: Number(m.eredmeny) || 0, _gyujto: true, _kulcs: 'm-er' },
      { oldal: 'Források', nev: 'FORRÁSOK ÖSSZESEN', osszeg: Number(m.forras_ossz) || 0, _gyujto: true, _kulcs: 'm-fo' },
    ];
  }
  get merlegEgyensuly(): boolean | null { return this.merleg ? !!this.merleg.egyensuly : null; }

  /*  ÁFA-ANALITIKA (kettős): kulcsonkénti forgalom + levonható / fizetendő / egyenleg. */
  private epitAfa() {
    this.oszlopok = [
      { k: 'datum', c: 'ÁFA-dátum' }, { k: 'biz', c: 'Bizonylatszám' }, { k: 'nev', c: 'Megnevezés / partner', szeles: true }, { k: 'szamla', c: 'Számla' },
      { k: 'kulcs', c: 'Kulcs' }, { k: 'fajta', c: 'Fajta' }, { k: 'irany', c: 'Irány' },
      { k: 'fizalap', c: 'Fizetendő alap', num: true, osszeg: true, cs: 'cs-afa' }, { k: 'fizafa', c: 'Fizetendő ÁFA', num: true, osszeg: true, cs: 'cs-afa' },
      { k: 'levalap', c: 'Levonható alap', num: true, osszeg: true }, { k: 'levafa', c: 'Levonható ÁFA', num: true, osszeg: true },
    ];
    const a = this.afa || {};
    const kulcsSor = (x: any) => ({ nev: `${x.kulcs === null ? 'Kulcs nélkül' : x.kulcs + '% ÁFA-kulcs'} – összesen (${x.db} tétel)`, kulcs: x.kulcs === null ? '' : x.kulcs + '%',
      fizalap: x.fizetendo_alap || null, fizafa: x.fizetendo_afa || null, levalap: x.levonhato_alap || null, levafa: x.levonhato_afa || null, _gyujto: true, _resz: true, _kulcs: 'ak' + x.kulcs });
    const tetel = (r: any, i: number) => {
      const fiz = r.irany === 'fizetendo';
      return { datum: r.afa_datum, biz: r.bizonylatszam || '', nev: r.partner_nev || r.megjegyzes || '', szamla: r.szamlaszam,
        kulcs: r.afa_kulcs === null ? '' : r.afa_kulcs + '%', fajta: r.fajta === 'afa' ? 'ÁFA' : 'alap', irany: fiz ? 'fizetendő' : 'levonható',
        fizalap: fiz && r.fajta === 'alap' ? r.osszeg : null, fizafa: fiz && r.fajta === 'afa' ? r.osszeg : null,
        levalap: !fiz && r.fajta === 'alap' ? r.osszeg : null, levafa: !fiz && r.fajta === 'afa' ? r.osszeg : null,
        _kulcs: 'at' + i + r.tetel_id, _fajta: 'tetel', _id: r.tetel_id };
    };
    this.sorok = [
      ...(a.kulcsok || []).map(kulcsSor),
      { nev: `Fizetendő ÁFA: ${Math.round(a.fizetendo || 0).toLocaleString('hu-HU')} · Levonható: ${Math.round(a.levonhato || 0).toLocaleString('hu-HU')} · Egyenleg: ${Math.round(a.egyenleg || 0).toLocaleString('hu-HU')} Ft`,
        _gyujto: true, _resz: true, _kulcs: 'a-ossz' },
      ...(a.tetelek || []).map(tetel),
    ];
  }

  /*  BEFEKTETETT ESZKÖZ NAPLÓ: az év eszközmozgásai időrendben, a nyitó és a záró állománnyal. A bruttó
   *  és az écs (pénzügyi eszköznél értékvesztés) VÁLTOZÁSA soronként, előjelesen – így az összegzés-sor
   *  az év mozgása, és nyitó + mozgás = záró. */
  private epitEszkozNaplo() {
    this.oszlopok = [
      { k: 'jel', c: '' }, { k: 'datum', c: 'Dátum' }, { k: 'leltari', c: 'Leltári sz.' }, { k: 'megnev', c: 'Eszköz', szeles: true },
      { k: 'csoport', c: 'Csoport' }, { k: 'esemeny', c: 'Esemény' }, { k: 'biz', c: 'Bizonylat' }, { k: 'megj', c: 'Megjegyzés', szeles: true },
      { k: 'brutto', c: 'Bruttó érték', num: true, osszeg: true, cs: 'cs-penz', cim: 'A bruttó (bekerülési) érték változása' },
      { k: 'leiras', c: 'Écs / értékvesztés', num: true, osszeg: true, cim: 'Az elszámolt értékcsökkenés – pénzügyi eszköznél az értékvesztés – változása' },
      { k: 'netto', c: 'Nettó érték', num: true, osszeg: true, cs: 'cs-penz', cim: 'A nettó (könyv szerinti) érték változása' },
      ...(this.kettos && !this.eszkozAdat?.kiva ? [{ k: 'tao', c: 'Écs (Tao)', num: true, osszeg: true, cim: 'A társasági adó szerinti écs' } as Oszlop] : []),
    ];
    const a = this.eszkozAdat || {};
    const { tol, ig } = this.szuro;
    const tetelek = (a.tetelek || []).filter((t: any) => (!tol || t.datum >= tol) && (!ig || t.datum <= ig));
    const allapotSor = (nev: string, x: any, k: string) => ({ megnev: nev, brutto: x?.brutto ?? null, leiras: x?.leiras ?? null, netto: x?.netto ?? null,
      _gyujto: true, _resz: true, _fejlec: true, _kulcs: k });
    this.sorok = [
      ...(a.nyito ? [allapotSor(`Nyitó állomány (${this.aktiv.ev - 1}. 12. 31.)`, a.nyito, 'en')] : []),
      ...tetelek.map((t: any, i: number) => ({
        jel: t.tetel_id ? this.jel(t.tetel_mod, t.tetel_id) : '', datum: t.datum, leltari: t.leltari_szam || '', megnev: t.megnevezes,
        csoport: t.csoport_nev, esemeny: t.esemeny, biz: t.bizonylatszam || '', megj: [t.partner, t.megjegyzes].filter(Boolean).join(' · '),
        brutto: t.brutto || null, leiras: t.leiras || null, netto: t.netto || null, tao: t.tao || null,
        _szlak: [t.fokonyvi_szamla, t.ecs_szamla].filter(Boolean).map(String), _partner: t.partner || '',
        _kulcs: 'ez' + i + t.eszkoz_id + (t.tetel_id || ''), _eszkoz: t.eszkoz_id,
        _fajta: t.tetel_id ? t.tetel_mod : null, _id: t.tetel_id || null, _naplo: t.naplo_tipus || 'vegyes',
      })),
      ...(a.zaro ? [allapotSor(`Záró állomány (${this.aktiv.ev}. 12. 31.)`, a.zaro, 'ez')] : []),
    ];
  }
  /*  MOZGÁSTÁBLA – csoportonként (és főcsoportonként) a bruttó érték és az écs / értékvesztés nyitója,
   *  növekedése, csökkenése és zárója, a nettó értékkel: a kiegészítő melléklet táblája. */
  private epitEszkozTabla() {
    this.oszlopok = [
      { k: 'nev', c: 'Csoport', szeles: true }, { k: 'db', c: 'Db', num: true, cim: 'Az év végén állományban lévő eszközök száma' },
      { k: 'bny', c: 'Bruttó nyitó', num: true, cs: 'cs-penz' }, { k: 'bno', c: 'Növekedés', num: true, cs: 'cs-penz' },
      { k: 'bcs', c: 'Csökkenés', num: true, cs: 'cs-penz' }, { k: 'bza', c: 'Bruttó záró', num: true, cs: 'cs-penz' },
      { k: 'lny', c: 'Écs nyitó', num: true }, { k: 'lno', c: 'Écs növ.', num: true, cim: 'Terv szerinti és terven felüli écs, értékvesztés' },
      { k: 'lcs', c: 'Écs csökk.', num: true, cim: 'Kivezetés, visszaírás' }, { k: 'lza', c: 'Écs záró', num: true },
      { k: 'nny', c: 'Nettó nyitó', num: true, cs: 'cs-penz' }, { k: 'nza', c: 'Nettó záró', num: true, cs: 'cs-penz' },
    ];
    this.sorok = (this.eszkozAdat?.mozgastabla || []).map((m: any) => ({
      nev: m.szint === 'csoport' ? '    ' + m.csoport_nev : m.csoport_nev, db: m.db,
      bny: m.brutto_nyito, bno: m.brutto_nov, bcs: m.brutto_csokk, bza: m.brutto_zaro,
      lny: m.leiras_nyito, lno: m.leiras_nov, lcs: m.leiras_csokk, lza: m.leiras_zaro,
      nny: m.netto_nyito, nza: m.netto_zaro,
      _gyujto: m.szint !== 'csoport', _kulcs: 'mt' + m.csoport,
    }));
  }
  /*  Az eszköz megnyitása a Tárgyi eszköz modulban (alrendszer-váltással, mint a menü csempéje). Az
   *  asztali programban az a MÁSIK program – azt indítjuk; ott a cég közös, az eszközt ki kell választani. */
  private eszkozNyit(r: any) {
    const asztali = (window as any).CRONOM_ASZTALI;
    if (asztali && String(asztali.mod || '').toUpperCase() !== 'ESZKOZ') { window.open('cronom-eszkoz://megnyit', '_blank'); return; }
    const k = this.keret.a;
    const mod = (k?.modok() || []).find((m) => m.name === 'ESZKOZ');
    if (!k || !mod) { this.uzen('Nincs jogosultságod a Tárgyi eszköz modulhoz.', true); return; }
    k.modValt(mod, `/eszkoz/eszkozok?eszkoz=${encodeURIComponent(r._eszkoz)}`);
  }

  /* ================= keresés, összegzés, export ================= */

  /*  NAGY NAPLÓK (user, 2026-11-03: „ahogy egyre nőnek, lefagy a program a töltés alatt, illetve ha szűrök”).
   *  Korábban a szűrt sorok, a látható oszlopok (minden sor × oszlop bejárása) és oszloponként az összeg
   *  getterek voltak – MINDEN változásdetektáláskor (kattintás, billentyű, egérgörgő) többször is újraszámolódtak
   *  a teljes évre, és a rács az összes sort egyszerre rajzolta ki. Most:
   *   – a három érték memoizált: csak akkor számolódik újra, ha a sorok / oszlopok / keresés változik;
   *   – a gyorskeresés gépelés után 250 ms-mal szűr (keresesValt), nem minden leütésnél;
   *   – a rács fokozatosan rajzol (RACS_LAP sor, a többi görgetésre / a „további” sorra kattintva – sorokMutat);
   *   – a sorok trackBy-jal (_kulcs) újrahasznosulnak.
   *  Az összegzés, a nyomtatás és az export továbbra is a TELJES szűrt listából dolgozik. */
  private _szurt: { sorok: any[] | null; oszlopok: Oszlop[] | null; k: string; mod: string; res: any[] } = { sorok: null, oszlopok: null, k: '', mod: '', res: [] };
  get lathatoSorok(): any[] {
    const k = this.kereses.trim().toLowerCase();
    const c = this._szurt;
    if (c.sorok === this.sorok && c.oszlopok === this.oszlopok && c.k === k && c.mod === this.keresesMod) return c.res;
    let res = this.sorok;
    if (k) {
      const szoveges = this.oszlopok.filter((o) => !o.num).map((o) => o.k);
      res = this.sorok.filter((r) => !r._resz && !r._fejlec && szoveges.some((key) => {
        const v = String(r[key] ?? '').toLowerCase();
        return this.keresesMod === 'eleje' ? v.startsWith(k) : v.includes(k);
      }));
    }
    this._szurt = { sorok: this.sorok, oszlopok: this.oszlopok, k, mod: this.keresesMod, res };
    return res;
  }

  private _osszegek: { sorok: any[] | null; map: Map<string, number> } = { sorok: null, map: new Map() };
  osszeg(o: Oszlop): number | null {
    if (!o.osszeg) return null;
    const sorok = this.lathatoSorok;
    if (this._osszegek.sorok !== sorok) this._osszegek = { sorok, map: new Map() };
    let v = this._osszegek.map.get(o.k);
    if (v === undefined) { v = sorok.filter((r) => !r._resz).reduce((a, r) => a + (Number(r[o.k]) || 0), 0); this._osszegek.map.set(o.k, v); }
    return v;
  }
  get osszegzesVan(): boolean { return this.oszlopok.some((o) => o.osszeg); }

  /** A képernyőn látható oszlopok: a teljesen üreseket (egyetlen sorban sincs értékük) elrejtjük. */
  private _oszl: { sorok: any[] | null; oszlopok: Oszlop[] | null; ures: boolean; res: Oszlop[] } = { sorok: null, oszlopok: null, ures: false, res: [] };
  get lathatoOszlopok(): Oszlop[] {
    if (this.uresOszlopok) return this.oszlopok;
    const sorok = this.lathatoSorok;
    const c = this._oszl;
    if (c.sorok === sorok && c.oszlopok === this.oszlopok && c.ures === this.uresOszlopok) return c.res;
    const res = !sorok.length ? this.oszlopok
      : this.oszlopok.filter((o) => o.k === 'jel' || sorok.some((r) => r[o.k] !== null && r[o.k] !== undefined && r[o.k] !== ''));
    this._oszl = { sorok, oszlopok: this.oszlopok, ures: this.uresOszlopok, res };
    return res;
  }
  get rejtettOszlopDb(): number { return this.oszlopok.length - this.lathatoOszlopok.length; }

  /** Gyorskeresés: a mező azonnal frissül, a szűrés (és a rács újrarajzolása) gépelés után 250 ms-mal. */
  keresesValt(v: string) {
    this.keresesBe = v;
    clearTimeout(this.keresesIdozito);
    this.keresesIdozito = setTimeout(() => { this.kereses = v; }, 250);
  }

  /*  FOKOZATOS RAJZOLÁS: egyszerre RACS_LAP sor kerül a DOM-ba; a rács aljához közeledve (görgetés) vagy a
   *  „további tételek” sorra kattintva jön a következő lap. A kijelölt sor (pl. visszatéréskor) mindig látszik. */
  megjelenitett = RACS_LAP;
  private _resz: { sorok: any[] | null; db: number; res: any[] } = { sorok: null, db: 0, res: [] };
  get rajzoltSorok(): any[] {
    const sorok = this.lathatoSorok;
    if (this._resz.sorok !== sorok) this.megjelenitett = RACS_LAP;   // új lista → újra az első lappal indul
    if (this.kijelolt && this.megjelenitett < sorok.length) {
      const i = sorok.indexOf(this.kijelolt);
      if (i >= this.megjelenitett) this.megjelenitett = Math.ceil((i + 1) / RACS_LAP) * RACS_LAP;
    }
    const c = this._resz;
    if (c.sorok === sorok && c.db === this.megjelenitett) return c.res;
    const res = sorok.length > this.megjelenitett ? sorok.slice(0, this.megjelenitett) : sorok;
    this._resz = { sorok, db: this.megjelenitett, res };
    return res;
  }
  get hatralevoSorDb(): number { return Math.max(0, this.lathatoSorok.length - this.megjelenitett); }
  sorokMutat(mind = false) { this.megjelenitett = mind ? this.lathatoSorok.length : this.megjelenitett + RACS_LAP; }
  racsGorget(e: Event) {
    const el = e.target as HTMLElement;
    if (this.hatralevoSorDb && el.scrollTop + el.clientHeight >= el.scrollHeight - 400) this.sorokMutat();
  }
  sorTrack = (_: number, r: any) => r?._kulcs ?? r;

  ertek(r: any, o: Oszlop): string {
    const v = r[o.k];
    if (v === null || v === undefined || v === '') return '';
    if (o.num) return this.szam(v);
    return String(v);
  }
  szam(v: any): string {
    const n = Number(v);
    if (!isFinite(n)) return '';
    // Ezres tagolás MINDIG (a magyar formátum 4 jegyűnél elhagyná: 5500 vs 12 000 egy oszlopban).
    const kerek = Math.abs(n - Math.round(n)) < 0.005;
    const [egesz, tort] = Math.abs(n).toFixed(kerek ? 0 : 2).split('.');
    return (n < 0 ? '−' : '') + egesz.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + (tort ? ',' + tort : '');
  }
  negativE(r: any, o: Oszlop): boolean { return !!o.num && Number(r[o.k]) < 0; }

  keresoFokusz() { setTimeout(() => this.keresoMezo?.nativeElement.focus(), 0); }

  /** Nyomtatás: önálló nyomtatási dokumentum (naplo-nyomtatas.ts) a LÁTHATÓ sorokból – a szűrés,
   *  a keresés és az összegzés pontosan úgy kerül papírra, ahogy a képernyőn áll. */
  async nyomtat() {
    let ugyfel: any = null;
    try {
      const lista: any[] = await this.naplo.get(`/ugyfelek/${this.aktiv.intezmeny}`);
      ugyfel = (lista || []).find((u) => u.id === this.aktiv.ugyfelId) || null;
    } catch { /* ügyféladat nélkül is nyomtatható */ }
    const um: any = await this.data.getFromStorage('userMetadata').catch(() => null);
    const cim = [ugyfel?.irsz, ugyfel?.telepules].filter(Boolean).join(' ') + (ugyfel?.cim ? ', ' + ugyfel.cim : '');
    const oszlopok = this.oszlopok.filter((o) => o.k !== 'jel');
    naploNyomtat({
      cim: this.ful === 'karton' ? `Főkönyvi karton – ${this.szuro.szamla} ${this.karton?.nev || ''}` : this.fulCim,
      ugyfel: { nev: this.aktiv.ugyfelNev, adoszam: ugyfel?.adoszam, cim: cim.replace(/^, /, '') },
      ev: this.aktiv.ev,
      mod: this.kettos ? 'Kettős könyvelés' : 'Egyszeres könyvelés',
      szurok: this.nyomtatasiSzurok(),
      oszlopok,
      sorok: this.lathatoSorok,
      ertek: (r, o) => this.ertek(r, o as Oszlop),
      osszegSor: this.osszegzes && this.osszegzesVan
        ? (o, i) => (o as Oszlop).osszeg ? this.szam(this.osszeg(o as Oszlop)) : (i === 0 ? 'Összesen' : '')
        : undefined,
      sorOsztaly: (r) => (r._gyujto ? 'gyujto' : ''),
      keszitette: [um?.viselt_vez, um?.viselt_ker].filter(Boolean).join(' '),
    });
  }

  /** A lapra kerülő szűrések, olvasható formában (csak ami ténylegesen szűkít). */
  private nyomtatasiSzurok(): [string, string][] {
    const s: [string, string][] = [];
    const { tol, ig, forras, statusz, tipus, allapot } = this.szuro;
    if (this.ful === 'bank') { const f = this.forrasok.find((x) => x.id === forras); if (f) s.push(['Forrás', f.nev]); }
    if (this.ful === 'hazipenztar' && this.kettos && this.hazipenztarSzamla) s.push(['Pénztár-számla', this.hazipenztarSzamla]);
    if (!['kivonat', 'gyorsmerleg', 'afa'].includes(this.ful) && (tol || ig)) s.push(['Időszak', `${tol || '…'} – ${ig || '…'}`]);
    if (this.ful === 'afa' && this.afaIdoszak) {
      s.push(['Időszak', `${this.aktiv.ev} – ${this.afaIdoszakCimke}`]);
      if (this.afaSzuro.irany) s.push(['Irány', this.afaSzuro.irany === 'fizetendo' ? 'fizetendő' : 'levonható']);
      if (this.afaSzuro.kulcs) s.push(['Kulcs', this.afaSzuro.kulcs + '%']);
      if (this.aranyositasLathato) {
        const a = this.aranyositas;
        s.push(['Levonási hányad', `${a.levonasi_hanyad} (levonható ${this.szam(a.levonhato)} Ft, le nem vonható ${this.szam(a.le_nem_vonhato)} Ft)`]);
      }
    }
    if (this.ful === 'bank' && statusz) s.push(['Státusz', statusz]);
    if (this.ful === 'penzugyi' && tipus) s.push(['Típus', tipus === 'vevo' ? 'Vevő' : 'Szállító']);
    if (this.ful === 'penzugyi' && allapot) s.push(['Állapot', { nyitott: 'Nyitott', lejart: 'Lejárt', rendezett: 'Rendezett' }[allapot] || allapot]);
    if (this.kereses.trim()) s.push(['Keresés', `„${this.kereses.trim()}" (${this.keresesMod === 'eleje' ? 'eleje egyezik' : 'tartalmazza'})`]);
    if ((this.ful === 'bank' || this.ful === 'penzugyi') && this.negativ) s.push(['Előjel', this.ful === 'bank' ? 'kiadás negatív' : 'szállító negatív']);
    return s;
  }

  exportal() {
    const oszl = this.oszlopok.filter((o) => o.k !== 'jel');
    const fej = oszl.map((o) => o.c);
    const csv = [fej, ...this.lathatoSorok.map((r) => oszl.map((o) => {
      const v = r[o.k];
      if (v === null || v === undefined) return '';
      return o.num ? String(v).replace('.', ',') : String(v);
    }))].map((sor) => sor.map((c) => /[;"\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c).join(';')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${this.ful}_${this.aktiv.ev}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  }

  /* ── NYOMTATVÁNYOK (pénzügyi nyilvántartás) – PDF-riportok egy másik könyvelőprogram nyomtatási
   *  ablakának mintájára. A PDF-et a backend állítja elő, KÖZÖS szerződés szerint:
   *    POST /api/naplo/penzugy-nyomtatvany/:ugyfelId?ev=YYYY  → application/pdf (blob).
   *  A végpont POST + body, ezért blobként kérjük le, és új böngészőlapon nyitjuk meg (a valódi
   *  nyomtatást a böngésző PDF-nézője végzi). A relatív '/api/…' úton az auth-interceptor a tokent
   *  ráteszi (mint a többi hívásnál). ── */
  nyomtatvanyNyitva = false;
  nyomtatvanyFut = false;
  nyomtatvanyForm = {
    format: 'szamla_lista', megjegyzes: '', idopont: true, datum: true, csikozas: true,
    csakSzurt: false, fordulonap: new Date().toISOString().slice(0, 10),
  };
  readonly nyomtatvanyFormatumok: { k: string; c: string }[] = [
    { k: 'reszletes_szamlasor', c: 'Részletes lista számlasorokkal' },
    { k: 'reszletes_kontir', c: 'Részletes lista kontírozással' },
    { k: 'szamla_lista', c: 'Számla lista' },
    { k: 'szamla_lista_partner', c: 'Számla lista partnerenként' },
    { k: 'szamla_lista_deviza', c: 'Számla lista (devizás)' },
    { k: 'szamla_lista_partner_deviza', c: 'Számla lista partnerenként (devizás)' },
    { k: 'korosit_vevo_szallito', c: 'Korosított vevő-szállító kimutatás' },
    { k: 'korosit_partner', c: 'Korosított tételek csop. partnerenként' },
    { k: 'korosit_partner_ujoldal', c: 'Korosított tételek csop. partnerenként új oldalra' },
    { k: 'korosit_deviza', c: 'Korosított tételek csoportosítása (devizás)' },
    { k: 'korosit_partner_ossz', c: 'Korosított lista partnerenként összesítve' },
    { k: 'szamla_kiegyenlites', c: 'Számla lista kiegyenlítésekkel' },
    { k: 'szamla_kiegyenlites_deviza', c: 'Számla lista kiegyenlítésekkel (devizás)' },
    { k: 'szamla_lista_partner_bank', c: 'Számla lista partnerenként bankszámlaszámmal' },
  ];
  /** A Fordulónap mező csak a korosit_* (korosított) formátumoknál látszik. */
  get nyomtatvanyKorosit(): boolean { return this.nyomtatvanyForm.format.startsWith('korosit'); }
  nyomtatvanyNyit() {
    this.nyomtatvanyForm.fordulonap = this.nyomtatvanyForm.fordulonap || new Date().toISOString().slice(0, 10);
    this.nyomtatvanyNyitva = true;
  }
  nyomtatvanyBezar() { this.nyomtatvanyNyitva = false; }
  /** A kiválasztott riport PDF-je új lapon (POST + blob). „Kiválasztott" esetén a látható sorok
   *  (lathatoSorok) kiegyenlítés-id-jeit küldjük, egyébként null = teljes lista. */
  async nyomtatvanyKeszit() {
    const u = this.aktiv.ugyfelId;
    if (!u || this.nyomtatvanyFut) return;
    const f = this.nyomtatvanyForm;
    const ids: string[] | null = f.csakSzurt
      ? this.lathatoSorok.map((r) => r._id).filter((x) => x != null).map((x) => String(x))
      : null;
    const body = {
      format: f.format, megjegyzes: f.megjegyzes || '',
      idopont: f.idopont, datum: f.datum, csikozas: f.csikozas,
      szuro: { tipus: this.szuro.tipus || '', allapot: this.szuro.allapot || '' },
      ids, fordulonap: this.nyomtatvanyKorosit ? (f.fordulonap || null) : null,
    };
    this.nyomtatvanyFut = true;
    try {
      const blob = await firstValueFrom(
        this.http.post(`/api/naplo/penzugy-nyomtatvany/${u}?ev=${this.aktiv.ev}`, body, { responseType: 'blob' }));
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      this.nyomtatvanyBezar();
    } catch (e: any) {
      this.uzen(await this.blobHiba(e, 'A nyomtatvány elkészítése nem sikerült.'), true);
    } finally {
      this.nyomtatvanyFut = false;
    }
  }
  /** Blob-válaszú kérés hibája: a szerver JSON hibaüzenetét a blobból olvassuk ki. */
  private async blobHiba(e: any, alap: string): Promise<string> {
    const b = e?.error;
    try {
      if (b instanceof Blob) { const sz = await b.text(); try { return JSON.parse(sz)?.error || alap; } catch { return sz || alap; } }
      if (typeof b === 'string') return b;
      if (b?.error) return b.error;
    } catch { /* marad az alapüzenet */ }
    return alap;
  }

  /** Sok oszlopnál tömörebb rács (kisebb betű és cellatávolság), hogy az összegek a képben maradjanak. */
  get tomor(): boolean { return this.lathatoOszlopok.length > 11; }

  sorOsztaly(r: any): Record<string, boolean> {
    return { gyujto: !!r._gyujto, albontas: !!r._alab, lejart: !!r._lejart, rendezett: this.ful === 'penzugyi' && !r._nyitott,
      athozott: this.ful === 'penzugyi' && !!r._athozott, tulfizetett: this.ful === 'penzugyi' && !!r._tulfizetett,
      pn_vevo: this.ful === 'penzugyi' && r._tipus === 'vevo', pn_szallito: this.ful === 'penzugyi' && r._tipus === 'szallito', kijelolt: this.kijelolt === r };
  }

  /* ================= sor-kijelölés és műveletek ================= */

  kijelol(r: any) { this.kijelolt = r; }
  dupla(r: any) { this.kijelolt = r; const m = this.muveletek.find((x) => x.k === 'modositas' || x.k === 'karton'); if (m) this.futtat(m); }

  /** Billentyűk a rácson: Enter = módosítás, Delete = törlés, Insert = új tétel (mezőben nem). */
  @HostListener('window:keydown', ['$event'])
  billentyu(e: KeyboardEvent) {
    const cel = e.target as HTMLElement;
    if (this.modal || /INPUT|SELECT|TEXTAREA/.test(cel?.tagName || '') || e.altKey || e.ctrlKey || e.metaKey) return;
    const k = e.key === 'Enter' ? 'modositas' : e.key === 'Delete' ? 'torles' : e.key === 'Insert' ? 'uj' : null;
    if (!k) return;
    const m = this.muveletek.find((x) => x.k === k || (k === 'uj' && x.k.startsWith('uj')));
    if (m && !this.tiltva(m)) { e.preventDefault(); this.futtat(m); }
  }

  //  MEMOIZÁLT a gombsor. Korábban a getter MINDEN változásdetektáláskor ÚJ tömböt + ÚJ objektumokat
  //  adott vissza, és a `*ngFor`-on nem volt trackBy → az Angular eldobta és ÚJRAGYÁRTOTTA a lenti
  //  gombokat minden CD-nél. Ha egy valódi egérlenyomás pont egy újraépítés alá esett, a `mousedown`
  //  és a `mouseup` MÁS elemre került, ezért a böngésző NEM generált `click` eseményt → a gomb
  //  „nem csinált semmit" (szaggatottan, sok helyen). A stabil referencia ezt megszünteti.
  private _muvKulcs = '';
  private _muvLista: Muvelet[] = [];
  get muveletek(): Muvelet[] {
    const k = `${this.ful}|${this.kettos}|${this.eszkozNezet}`;
    if (k !== this._muvKulcs) { this._muvKulcs = k; this._muvLista = this.muveletekSzamol(); }
    return this._muvLista;
  }
  /** A gombsor kiszámítása (a fül/mód szerint) – csak akkor hívódik, ha változott a kulcs. */
  private muveletekSzamol(): Muvelet[] {
    const kettos = this.kettos;
    const nincsKettos = (r: any) => (r && !r._id ? 'Ehhez a sorhoz nincs bizonylat.' : null);
    const szamlaKep = { k: 'szamlakep', c: 'Számlakép megtekintése', ikon: 'document-text-outline', sorKell: true,
      tilt: (r: any) => (this.kepElerheto(r) ? null : 'Ehhez a sorhoz nincs számlakép.') };
    const jegyzet = { k: 'jegyzet', c: 'Jegyzet', ikon: 'create-outline', sorKell: true, tilt: (r: any) => (this.jegyzetKulcs(r) ? null : 'Ehhez a sorhoz nem fűzhető jegyzet.') };
    const bankTort = { k: 'bankTortenet', c: 'Bankszámla történet', ikon: 'person-circle-outline', sorKell: true, tilt: (r: any) => (r?._partner ? null : 'A sorhoz nem tartozik partner.') };
    switch (this.ful) {
      case 'penzugyi':
        return [
          { k: 'ujVevo', c: 'Új vevő tétel', ikon: 'add-circle-outline', sorKell: false },
          { k: 'ujSzallito', c: 'Új száll. tétel', ikon: 'add-circle-outline', sorKell: false },
          { k: 'modositas', c: 'Módosítás', ikon: 'create-outline', sorKell: true },
          { k: 'torles', c: 'Tétel törlése', ikon: 'trash-outline', sorKell: true },
          { k: 'kiegyKonyv', c: 'Kiegyenlítés könyvelése', ikon: 'cash-outline', sorKell: true, tilt: (r) => (r?._nyitott ? null : 'A számla már rendezett.') },
          { k: 'kiegyBeall', c: 'Kiegyenlítés beállítása', ikon: 'options-outline', sorKell: true },
          { k: 'rendezesek', c: 'Rendezések', ikon: 'list-outline', sorKell: true },
          { k: 'atertekeles', c: 'Átértékelés', ikon: 'swap-vertical-outline', sorKell: true,
            tilt: () => 'Devizás átértékelés (év végi MNB-árfolyamon) – még nincs: az árfolyam-dátum és a számlák egyeztetése után készül.' },
          szamlaKep, bankTort, jegyzet,
        ];
      case 'bank':
        return [
          { k: 'ujPenztar', c: 'Új pénztári tétel', ikon: 'add-circle-outline', sorKell: false },
          { k: 'ujBank', c: 'Új banki tétel', ikon: 'add-circle-outline', sorKell: false },
          { k: 'modositas', c: 'Módosítás', ikon: 'create-outline', sorKell: true },
          { k: 'torles', c: 'Tétel törlése', ikon: 'trash-outline', sorKell: true,
            tilt: (r) => (r?._fajta === 'bank' && !r._tetel ? 'A banki tétel a bankszinkronból jön; könyvelés nélkül nincs mit törölni (a kontírt a „Hivatkozás törlése" veszi vissza).' : null) },
          { k: 'hivatkozasTorles', c: 'Hivatkozás törlése', ikon: 'unlink-outline', sorKell: true,
            tilt: (r) => (r?._fajta === 'bank' && r._statusz && r._statusz !== 'uj' ? null : 'Nincs visszavonható párosítás / kontír.') },
          szamlaKep, bankTort, jegyzet,
        ];
      case 'vegyes': case 'nyito': case 'zaro': case 'osszes': case 'karton': case 'hazipenztar':
        if (!kettos) {   // egyszeres házipénztár
          return [
            { k: 'uj', c: 'Új pénztári tétel', ikon: 'add-circle-outline', sorKell: false },
            { k: 'modositas', c: 'Módosítás', ikon: 'create-outline', sorKell: true },
            { k: 'torles', c: 'Tétel törlése', ikon: 'trash-outline', sorKell: true },
            szamlaKep, bankTort, jegyzet,
          ];
        }
        return [
          { k: 'uj', c: this.ful === 'hazipenztar' ? 'Új pénztári tétel' : 'Új tétel', ikon: 'add-circle-outline', sorKell: false,
            tilt: () => (this.ful === 'nyito' || this.ful === 'zaro' ? 'A nyitó és a záró naplót az évnyitás / évzárás állítja elő (Könyvelés → Nyitás / zárás).' : null) },
          { k: 'modositas', c: 'Módosítás', ikon: 'create-outline', sorKell: true, tilt: nincsKettos },
          { k: 'torles', c: 'Tétel törlése', ikon: 'trash-outline', sorKell: true, tilt: nincsKettos },
          { k: 'osszepontozas', c: 'Utólagos összepontozás', ikon: 'git-merge-outline', sorKell: true, tilt: nincsKettos },
          ...(this.ful === 'osszes' || this.ful === 'vegyes' ? [] : [bankTort]),
          szamlaKep, jegyzet,
        ];
      case 'naplofokonyv':
        return [
          { k: 'uj', c: 'Új tétel', ikon: 'add-circle-outline', sorKell: false },
          { k: 'modositas', c: 'Módosítás', ikon: 'create-outline', sorKell: true },
          { k: 'torles', c: 'Tétel törlése', ikon: 'trash-outline', sorKell: true },
          szamlaKep, bankTort, jegyzet,
        ];
      case 'kivonat': case 'gyorsmerleg':
        return [{ k: 'karton', c: 'Főkönyvi karton', ikon: 'reader-outline', sorKell: true, tilt: (r) => (r?._szamla ? null : 'Válassz egy számla-sort.') }];
      case 'eszkoz':
        if (this.eszkozNezet === 'tabla') return [];
        return [
          { k: 'modositas', c: 'Bizonylat megnyitása', ikon: 'document-text-outline', sorKell: true,
            tilt: (r) => (r?._id ? null : 'Ehhez a sorhoz nincs bizonylat (pl. nyilvántartási tétel, vagy a beszerzés számlája nincs az eszközhöz kötve).') },
          { k: 'eszkozNyit', c: 'Eszköz megnyitása', ikon: 'construct-outline', sorKell: true, tilt: (r) => (r?._eszkoz ? null : 'Válassz egy eszköz-sort.') },
          jegyzet,
        ];
      default:
        return [];
    }
  }

  /** trackBy a gombsorhoz: a DOM a kulcs (m.k) szerint újrahasznosul, nem épül újra (lásd a getter megjegyzését). */
  muvTrack = (_: number, m: Muvelet) => m.k;

  tiltva(m: Muvelet): string | null {
    if (m.sorKell && !this.kijelolt) return 'Előbb válassz ki egy sort.';
    return m.tilt ? m.tilt(this.kijelolt) : null;
  }

  private kepElerheto(r: any): boolean {
    if (!r) return false;
    if (r._fajta === 'kiegy') return !!r.biz;
    if (r._fajta === 'bank') return !!r._paroztt;
    return !!(r._kep || r._osa);
  }
  private jegyzetKulcs(r: any): { forras: string; id: string } | null {
    if (!r?._id) return null;
    const forras = r._fajta === 'kiegy' ? 'kiegy' : r._fajta === 'bank' ? 'bank' : r._fajta === 'kettos' ? 'kettos' : r._fajta === 'egyszeres' ? 'egyszeres' : null;
    return forras ? { forras, id: String(r._id) } : null;
  }

  async futtat(m: Muvelet) {
    const ok = this.tiltva(m);
    if (ok) { this.uzen(ok, true); return; }
    const r = this.kijelolt;
    const u = this.aktiv.ugyfelId!;
    try {
      switch (m.k) {
        case 'ujVevo': return this.kettos ? this.kontirba({ naplo: 'vevo' }) : this.kiegySzerkNyit(null, 'vevo');
        case 'ujSzallito': return this.kettos ? this.kontirba({ naplo: 'szallito' }) : this.kiegySzerkNyit(null, 'szallito');
        case 'ujPenztar': return this.kettos ? this.kontirba({ naplo: 'penztar' }) : this.egyszeresbe({ uj: 'penztar' });
        case 'ujBank': return this.kettos ? this.kontirba({ naplo: 'bank' }) : this.egyszeresbe({ uj: 'bank' });
        case 'uj':
          if (!this.kettos) return this.egyszeresbe({ uj: this.ful === 'hazipenztar' ? 'penztar' : 'kiadas' });
          return this.kontirba({ naplo: this.ful === 'hazipenztar' ? 'penztar' : 'vegyes' });
        case 'modositas': return this.modosit(r);
        case 'torles': return this.torol(r);
        case 'hivatkozasTorles': return this.hivatkozasTorol(r);
        case 'kiegyKonyv': return this.kiegyKonyvNyit(r);
        case 'kiegyBeall': return this.kiegyBeallNyit(r);
        case 'rendezesek': return this.rendezesekNyit(r);
        case 'szamlakep': return this.szamlakep(r);
        case 'bankTortenet': return this.bankTortenetNyit(r);
        case 'jegyzet': return this.jegyzetNyit(r);
        case 'osszepontozas': return this.osszepontozasNyit(r);
        case 'karton': return this.fulValt('karton', { szamla: r._szamla });
        case 'eszkozNyit': return this.eszkozNyit(r);
      }
    } catch (e: any) { this.uzen(e?.error?.error || e?.message || 'A művelet nem sikerült.', true); }
  }

  /*  A könyvelőablak a Naplókból nyílik: `vissza` = ide térjen vissza (a könyvelőablakban
   *  „Vissza a naplóhoz" gomb, és mentés után magától visszajön, a tételt kijelölve). */
  private get visszaUt(): string {
    const q = new URLSearchParams({ ful: this.ful });
    // Módosításnál a KIVÁLASZTOTT sor legyen visszatéréskor is kijelölve (a pénzügyi nyilvántartás
    // sora a számla, nem a bizonylat – ezért a sor saját azonosítója megy).
    if (this.kijelolt?._id) q.set('kijelol', String(this.kijelolt._id));
    if (this.ful === 'bank' && this.szuro.forras) q.set('forras', this.szuro.forras);
    if (this.ful === 'karton' && this.szuro.szamla) q.set('szamla', this.szuro.szamla);
    return '/naplo/naplok?' + q.toString();
  }
  /** A kettős könyvelőablak a megadott naplóval (és bizonylattal). */
  private kontirba(p: { naplo?: string; tetel?: string }) {
    this.router.navigate(['/naplo/kettos'], { queryParams: { nezet: 'kontir', ...p, ...(p.tetel ? {} : { uj: '1' }), vissza: this.visszaUt } });
  }
  private egyszeresbe(p: { uj?: string; tetel?: string }) { this.router.navigate(['/naplo/konyveles'], { queryParams: { ...p, vissza: this.visszaUt } }); }

  private async modosit(r: any) {
    if (r._fajta === 'kettos') return this.kontirba({ naplo: r._naplo, tetel: r._id });
    if (r._fajta === 'egyszeres') return this.egyszeresbe({ tetel: r._id });
    if (r._fajta === 'bank') {
      //  Kettősben HELYBEN, ablakban módosítható (mint a pénzügyi nyilvántartás), nem dob át a könyvelőoldalra
      //  (user, 2026-10-07). Egyszeresben a naplófőkönyv szerkesztője nyílik, ahogy eddig.
      if (r._tetel && !this.kettos) return this.egyszeresbe({ tetel: r._tetel });
      return this.bankSzerkNyit(r);
    }
    if (r._fajta === 'kiegy') {
      if (this.kettos && r.biz) {
        // A SZÁMLA bizonylatát keressük (vevő/szállító napló) – ugyanazzal a számmal a kiegyenlítő
        // banki (vegyes) bizonylat is létezik, azt nem ez a sor jelenti.
        const t: any = await this.kGet(`/tetel-bizonylat/${this.aktiv.ugyfelId}?biz=${encodeURIComponent(r.biz)}&naplo=${r._tipus}`).catch(() => null);
        if (t?.id) return this.kontirba({ naplo: t.naplo_tipus, tetel: t.id });
      }
      return this.kiegySzerkNyit(r, r._tipus);
    }
  }

  private async megerosit(fejlec: string, uzenet: string, gomb = 'Törlés'): Promise<boolean> {
    return new Promise((resolve) => {
      this.alert.create({ header: fejlec, message: uzenet,
        buttons: [{ text: 'Mégse', role: 'cancel', handler: () => resolve(false) }, { text: gomb, role: 'destructive', handler: () => resolve(true) }] })
        .then((a) => a.present());
    });
  }

  private async torol(r: any) {
    const u = this.aktiv.ugyfelId;
    if (r._fajta === 'kettos' || (r._fajta === 'bank' && r._tetel && this.kettos)) {
      const id = r._fajta === 'kettos' ? r._id : r._tetel;
      if (!(await this.megerosit('Bizonylat törlése', `Törlöd a(z) ${r.nsz || r.biz || 'kiválasztott'} bizonylatot? A hozzá tartozó nyitott tétel, a kézi rendezések és a banki könyvelés-kapcsolat is visszaáll.`))) return;
      await this.nn('DELETE', `/bizonylat/${u}/${id}`);
    } else if (r._fajta === 'egyszeres' || (r._fajta === 'bank' && r._tetel)) {
      const id = r._fajta === 'egyszeres' ? r._id : r._tetel;
      if (!(await this.megerosit('Tétel törlése', 'Törlöd a kiválasztott naplófőkönyvi tételt?'))) return;
      await this.naplo.del(`/tetel/${id}`);
    } else if (r._fajta === 'kiegy') {
      if (this.kettos && r.biz) {
        const t: any = await this.kGet(`/tetel-bizonylat/${u}?biz=${encodeURIComponent(r.biz)}&naplo=${r._tipus}`).catch(() => null);
        if (t?.id) {
          //  Ugyanarra a számlára több bizonylat (duplikált könyvelés): csak a legutóbbi törlődik, a számla marad.
          const uzenet = Number(t.db) > 1
            ? `A(z) ${r.biz} számlához ${t.db} könyvelt bizonylat tartozik (duplikált könyvelés). A legutóbbit törlöm (${t.naplo_tipus === 'vevo' ? 'vevői' : 'szállítói'} napló ${t.sorszam}., rögzítve ${String(t.letrehozva || '').slice(0, 10)}); a számla és a kiegyenlítése a másik bizonylattal marad. Folytatod?`
            : `A(z) ${r.biz} számla könyvelt bizonylattal együtt törlődik. Folytatod?`;
          if (!(await this.megerosit('Tétel törlése', uzenet))) return;
          await this.nn('DELETE', `/bizonylat/${u}/${t.id}`);
          this.uzen('Törölve.'); await this.betolt(); return;
        }
      }
      if (Number(r._nyers?.kiegyenlitett) > 0.005) { this.uzen('A számlára már van kiegyenlítés – előbb a Rendezéseknél vond vissza.', true); return; }
      if (!(await this.megerosit('Tétel törlése', `Törlöd a(z) ${r.biz || ''} számlát a pénzügyi nyilvántartásból?`))) return;
      await this.naplo.del(`/kiegy/${r._id}`);
    }
    this.uzen('Törölve.');
    this.kijelolt = null;
    await this.betolt();
  }

  private async hivatkozasTorol(r: any) {
    const u = this.aktiv.ugyfelId;
    const konyvelt = r._statusz === 'konyvelt' && r._tetel;
    if (!(await this.megerosit('Hivatkozás törlése', konyvelt
      ? 'A banki tétel könyvelt: a könyvelt bizonylat törlődik, a párosítás és a kontír visszavonódik (a számlák hátraléka visszaáll).'
      : 'A banki tétel párosítása / kontírja visszavonódik (a számlák hátraléka visszaáll).', 'Visszavonás'))) return;
    if (konyvelt && this.kettos) await this.nn('DELETE', `/bizonylat/${u}/${r._tetel}`);
    await firstValueFrom(this.http.post(`/api/naplo-bank/${u}/kontir-visszavon/${r._id}${konyvelt && !this.kettos ? '?force=1' : ''}`, {}));
    this.uzen('A hivatkozás törölve.');
    await this.betolt();
  }

  private async szamlakep(r: any) {
    const u = this.aktiv.ugyfelId!;
    if (r._fajta === 'kettos' && r._kep) { this.data.megnyitFajl('/api/kettos/kep/' + r._id, 'Számlakép'); return; }
    if (r._fajta === 'egyszeres' && r._kep) { this.data.megnyitFajl('/api/naplo/osa/kep/' + r._id, 'Számlakép'); return; }
    let szamlaszam = r._osa || '', irany: 'inbound' | 'outbound' = r._osaIrany === 'outbound' ? 'outbound' : 'inbound';
    if (r._fajta === 'kiegy') { szamlaszam = r.biz; irany = r._tipus === 'vevo' ? 'outbound' : 'inbound'; }
    if (r._fajta === 'bank') { szamlaszam = String(r._paroztt || '').split(',')[0].trim(); irany = r._osszegNyers >= 0 ? 'outbound' : 'inbound'; }
    if (!szamlaszam) { this.uzen('Ehhez a sorhoz nincs számlakép.', true); return; }
    try { await osaSzamlakepNyit(this.naplo, u, szamlaszam, irany); }
    catch (e: any) { this.uzen(e?.error?.error || 'A számlakép nem érhető el (a számla nincs a NAV-tól letöltött számlák között).', true); }
  }

  /* ---------- ablakok ---------- */

  zar() { this.modal = null; this.m = {}; this.mFut = false; this.zarEngedd('zarBank'); this.zarEngedd('zarKiegy'); }
  get modalCim(): string {
    return ({ jegyzet: 'Jegyzet', kiegyKonyv: 'Kiegyenlítés könyvelése', kiegyBeall: 'Kiegyenlítés beállítása', rendezesek: 'Rendezések',
      bankTortenet: 'Bankszámla-történet', osszepontozas: 'Utólagos összepontozás',
      kiegySzerk: this.m?.id ? 'Számla módosítása' : 'Új számla a pénzügyi nyilvántartásba',
      bankSzerk: this.m?.konyveletlen ? 'Könyveletlen banki tétel' : 'Banki tétel módosítása' } as Record<string, string>)[this.modal || ''] || '';
  }

  jegyzetNyit(r: any) {
    const k = this.jegyzetKulcs(r)!;
    this.m = { ...k, cim: r.biz || r.nsz || r.partner || '', szoveg: this.jegyzetek.get(`${k.forras}|${k.id}`) || '' };
    this.modal = 'jegyzet';
  }
  async jegyzetMent() {
    this.mFut = true;
    try {
      await this.nn('PUT', `/jegyzet/${this.aktiv.ugyfelId}`, { forras: this.m.forras, ref_id: this.m.id, szoveg: this.m.szoveg });
      this.uzen(this.m.szoveg.trim() ? 'Jegyzet mentve.' : 'Jegyzet törölve.');
      this.zar(); await this.jegyzetekBetolt(); this.epit();
    } catch (e: any) { this.mFut = false; this.uzen(e?.error?.error || 'A jegyzet mentése nem sikerült.', true); }
  }

  kiegyKonyvNyit(r: any) {
    const k = r._nyers;
    const hatralek = Math.round((Number(k.brutto) - Number(k.kiegyenlitett)) * 100) / 100;
    const devizas = !!(k.deviza && k.deviza !== 'HUF');
    this.m = { k, hatralek, devizas, datum: new Date().toISOString().slice(0, 10), osszeg: hatralek, modja: 'bank',
      konyvel: this.kettos && !devizas, bizonylatszam: '', megjegyzes: '' };
    this.modal = 'kiegyKonyv';
  }
  async kiegyKonyvMent() {
    this.mFut = true;
    try {
      const r: any = await this.nn('POST', `/kiegyenlites/${this.aktiv.ugyfelId}/${this.m.k.id}`, {
        datum: this.m.datum, osszeg: this.m.osszeg, modja: this.m.modja, konyvel: this.m.konyvel && this.kettos && !this.m.devizas,
        bizonylatszam: this.m.bizonylatszam, megjegyzes: this.m.megjegyzes,
      });
      this.uzen(r?.tetel_id ? 'Kiegyenlítés rögzítve, a bizonylat elkészült.' : 'Kiegyenlítés rögzítve.');
      this.zar(); await this.betolt();
    } catch (e: any) { this.mFut = false; this.uzen(e?.error?.error || 'Nem sikerült rögzíteni.', true); }
  }

  kiegyBeallNyit(r: any) {
    const k = r._nyers;
    this.m = { k, kiegyenlitett: Number(k.kiegyenlitett) || 0, fizetesi_hatarido: k.fizetesi_hatarido || '', megjegyzes: k.megjegyzes || '', indok: '' };
    this.modal = 'kiegyBeall';
  }
  async kiegyBeallMent() {
    this.mFut = true;
    try {
      await this.nn('PUT', `/kiegyenlites-beallitas/${this.aktiv.ugyfelId}/${this.m.k.id}`, {
        kiegyenlitett: this.m.kiegyenlitett, fizetesi_hatarido: this.m.fizetesi_hatarido || null, megjegyzes: this.m.megjegyzes, indok: this.m.indok,
      });
      this.uzen('Kiegyenlítés beállítva.');
      this.zar(); await this.betolt();
    } catch (e: any) { this.mFut = false; this.uzen(e?.error?.error || 'Nem sikerült beállítani.', true); }
  }

  async rendezesekNyit(r: any) {
    this.m = { betolt: true, szamla: null, rendezesek: [] };
    this.modal = 'rendezesek';
    try { const d: any = await this.nn('GET', `/rendezesek/${this.aktiv.ugyfelId}/${r._id}`); this.m = { ...d, betolt: false, kiegyId: r._id }; }
    catch (e: any) { this.m.betolt = false; this.uzen(e?.error?.error || 'Nem sikerült betölteni.', true); }
  }
  async rendezesVissza(x: any) {
    if (!(await this.megerosit('Rendezés visszavonása', `Visszavonod a(z) ${x.datum} – ${this.szam(x.osszeg)} rendezést?${x.tetel_id && x.modja !== 'vegyes' ? ' A hozzá készült bizonylat is törlődik.' : ''}`, 'Visszavonás'))) return;
    try {
      await this.nn('DELETE', `/rendezes/${this.aktiv.ugyfelId}/${x.id}`);
      this.uzen('A rendezés visszavonva.');
      await this.rendezesekNyit({ _id: this.m.kiegyId });
      await this.betolt();
    } catch (e: any) { this.uzen(e?.error?.error || 'Nem sikerült visszavonni.', true); }
  }
  modjaNev(m: string): string {
    return ({ penztar: 'pénztár', bank: 'bank', kezi: 'kézi', beszamitas: 'beszámítás', vegyes: 'összepontozás (vegyes)', beallitas: 'beállítás' } as any)[m] || m;
  }

  async bankTortenetNyit(r: any) {
    this.m = { partner: r._partner, betolt: true };
    this.modal = 'bankTortenet';
    try {
      const d: any = await this.nn('GET', `/partner-bank/${this.aktiv.ugyfelId}?partner=${encodeURIComponent(r._partner)}`);
      this.m = { ...d, betolt: false };
    } catch (e: any) { this.m.betolt = false; this.uzen(e?.error?.error || 'Nem sikerült betölteni.', true); }
  }

  async osszepontozasNyit(r: any) {
    this.m = { betolt: true, tetelId: r._id };
    this.modal = 'osszepontozas';
    try {
      const d: any = await this.nn('GET', `/osszepontozas/${this.aktiv.ugyfelId}/${r._id}`);
      this.m = { ...d, betolt: false, tetelId: r._id, valaszt: {} as Record<string, number | null> };
    } catch (e: any) { this.m.betolt = false; this.uzen(e?.error?.error || 'Nem sikerült betölteni.', true); }
  }
  pontValt(n: any, be: boolean) {
    if (!be) { delete this.m.valaszt[n.id]; return; }
    const marad = Math.max(0, this.m.pontozhato - this.pontOsszeg);
    this.m.valaszt[n.id] = Math.min(n.hatralek, marad) || n.hatralek;
  }
  get pontOsszeg(): number { return (Object.values(this.m?.valaszt || {}) as any[]).reduce((a: number, v: any) => a + (Number(v) || 0), 0); }
  async pontMent() {
    const parok = Object.entries(this.m.valaszt).map(([kiegy_id, osszeg]) => ({ kiegy_id, osszeg })).filter((p: any) => Number(p.osszeg) > 0);
    if (!parok.length) { this.uzen('Jelölj ki legalább egy számlát.', true); return; }
    this.mFut = true;
    try {
      await this.nn('POST', `/osszepontozas/${this.aktiv.ugyfelId}/${this.m.tetelId}`, { parok });
      this.uzen('Összepontozva.');
      const id = this.m.tetelId; this.mFut = false;
      await this.osszepontozasNyit({ _id: id });
    } catch (e: any) { this.mFut = false; this.uzen(e?.error?.error || 'Nem sikerült.', true); }
  }
  async pontVissza(x: any) {
    try { await this.nn('DELETE', `/rendezes/${this.aktiv.ugyfelId}/${x.id}`); await this.osszepontozasNyit({ _id: this.m.tetelId }); }
    catch (e: any) { this.uzen(e?.error?.error || 'Nem sikerült visszavonni.', true); }
  }

  /** Számla felvétele / módosítása a pénzügyi nyilvántartásban (egyszeresnél és könyvelés nélküli számlánál). */
  kiegySzerkNyit(r: any | null, tipus: string) {
    const k = r?._nyers;
    this.zarEngedd('zarBank');
    this.m = k ? { ...k } : { tipus, partner_nev: '', bizonylatszam: '', kelt: new Date().toISOString().slice(0, 10), teljesites: '', fizetesi_hatarido: '', afa_esedekesseg: '', netto: null, afa: null, brutto: null, kiegyenlitett: 0, megjegyzes: '' };
    this.modal = 'kiegySzerk';
    if (k?.id) this.zarKer('zarKiegy', 'naplo_kiegy', k.id, () => this.kiegyUjratolt(k.id));
    else this.zarEngedd('zarKiegy');   // új számla – nincs zár
  }
  /** A zár felszabadulásakor / „Újratöltés”-re: a számla friss adata, ha az ablak még ezt mutatja. */
  private async kiegyUjratolt(id: string) {
    const lista: any[] = await this.naplo.get(`/kiegy/${this.aktiv.ugyfelId}${this.naplo.evq()}`);
    const k = (lista || []).find((x: any) => x.id === id);
    if (k && this.modal === 'kiegySzerk' && this.m?.id === id) this.m = { ...k };
  }
  /*  BANKI TÉTEL MÓDOSÍTÁSA HELYBEN (kettős): a bizonylat fejléce (dátum, bizonylatszám, partner, megnevezés)
   *  és a T/K sorai egy ablakban; mentés ugyanazon a végponton, mint a könyvelőablaké (POST /kettos/tetel –
   *  ott fut a gyűjtőszámla-, a nyitó/záró- és az egyensúly-ellenőrzés, és az egész forintra kerekítés). A
   *  többi fejlécmező (deviza, munkaszám, ÁFA-dátumok, számlakép) változatlanul megy vissza. A könyveletlen
   *  banki tételnél nincs mit módosítani: az ablak a kontírozásra visz. */
  async bankSzerkNyit(r: any) {
    this.zarEngedd('zarKiegy');
    if (!r._tetel) {
      this.zarEngedd('zarBank');
      this.m = { konyveletlen: true, r };
      this.modal = 'bankSzerk';
      return;
    }
    //  A szerkesztési zárat a betöltéssel párhuzamosan kérjük; ha a betöltés nem sikerül, elengedjük.
    const id = String(r._tetel);
    this.zarKer('zarBank', 'kettos_tetel', id, () => this.bankUjratolt(id));
    const kor = this.zarKor['zarBank'];
    let t: any;
    try { t = await this.kGet(`/tetel/${id}`); }
    catch (e) { if (kor === this.zarKor['zarBank']) this.zarEngedd('zarBank'); throw e; }
    this.m = this.bankM(t);
    this.modal = 'bankSzerk';
  }
  private bankM(t: any) {
    return {
      t, datum: t.datum, bizonylatszam: t.bizonylatszam || '', partner_nev: t.partner_nev || '', megnevezes: t.megnevezes || '',
      sorok: (t.sorok || []).map((s: any) => ({ ...s, tartozik: Number(s.tartozik) || 0, kovetel: Number(s.kovetel) || 0 })),
    };
  }
  /** A zár felszabadulásakor / „Újratöltés”-re: a bizonylat friss adata, ha az ablak még ezt mutatja. */
  private async bankUjratolt(id: string) {
    const t: any = await this.kGet(`/tetel/${id}`);
    if (this.modal === 'bankSzerk' && String(this.m?.t?.id) === id) this.m = this.bankM(t);
  }
  bankSor(): void { this.m.sorok.push({ szamlaszam: '', tartozik: 0, kovetel: 0, megjegyzes: '' }); }
  bankSorTorol(i: number): void { this.m.sorok.splice(i, 1); }
  bankOsszeg(o: 'tartozik' | 'kovetel'): number { return Math.round((this.m.sorok || []).reduce((a: number, s: any) => a + (Number(s[o]) || 0), 0)); }
  get bankEgyensuly(): boolean { return !!this.m?.sorok?.length && this.bankOsszeg('tartozik') === this.bankOsszeg('kovetel') && this.bankOsszeg('tartozik') > 0; }
  bankTeljesSzerkeszto(): void {
    const t = this.m?.t; this.zar();
    if (t) this.kontirba({ naplo: t.naplo_tipus || 'bank', tetel: t.id }); else this.router.navigate(['/naplo/kontir']);
  }
  async bankSzerkMent() {
    if (this.zarTilt(this.zarBank)) return;
    if (!this.bankEgyensuly) { this.uzen('A tartozik és a követel összege nem egyezik.', true); return; }
    this.mFut = true;
    try {
      const t = this.m.t;
      const sorok = this.m.sorok.filter((s: any) => String(s.szamlaszam || '').trim() && ((Number(s.tartozik) || 0) || (Number(s.kovetel) || 0)));
      const body = { ...t, devizas: !!t.deviza, datum: this.m.datum, bizonylatszam: this.m.bizonylatszam, partner_nev: this.m.partner_nev,
        megnevezes: this.m.megnevezes, sorok };
      await firstValueFrom(this.http.post(`/api/kettos/tetel/${this.aktiv.ugyfelId}?ev=${t.ev}`, body));
      this.uzen('A banki tétel mentve.');
      this.zar(); await this.betolt();
    } catch (e: any) { this.mFut = false; this.uzen(e?.error?.error || 'Nem sikerült menteni.', true); }
  }

  async kiegySzerkMent() {
    if (this.zarTilt(this.zarKiegy)) return;
    this.mFut = true;
    try {
      const b = { ...this.m, ugyfel_id: this.aktiv.ugyfelId };
      if (this.m.id) await this.naplo.put(`/kiegy/${this.m.id}`, b);
      else await this.naplo.post(`/kiegy${this.naplo.evq()}`, b);
      this.uzen('Mentve.');
      this.zar(); await this.betolt();
    } catch (e: any) { this.mFut = false; this.uzen(e?.error?.error || 'Nem sikerült menteni.', true); }
  }

  private async uzen(message: string, hiba = false) {
    (await this.toast.create({ message, duration: hiba ? 4000 : 2200, color: hiba ? 'danger' : 'success', position: 'top' })).present();
  }
}
